import PasswordInput from './PasswordInput';
import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase, getAllProfiles, getHolidays, addHoliday, deleteHoliday, getLocations,
  upsertSchedules, getAllSchedules, getFilteredRecords, getRecordsByMonth, runAlerts,
  adminEditRecord, adminAddManualRecord, localDateISO, getGeoPos, attachCompensation, saveCompensation, confirmEmailElsewhere } from '../lib/supabase';
import { getHolidaysForYear } from '../data/holidays';
import EmployeeProfileModal from './EmployeeProfileModal';
import ExportButton from './ExportButton';
import NotificationBell from './NotificationBell';
import MonthCalendar from './MonthCalendar';
import AuditHistory from './AuditHistory';
import { hasPermission, AdminPermissionsEditor, ALL_PERMISSIONS, materialize, summarize } from './AdminPermissions';
import AdminRoles from './AdminRoles';
import { listRoles } from '../lib/adminRoles';
import HorasExtra, { calcMonto, calcRate } from './HorasExtra';
import logoMark from '../assets/logo-gestion360.png';
import { BRAND } from '../config/brand';
import VacacionesAdmin from './VacacionesAdmin';
import TareasAdmin from './TareasAdmin';
import LicenciasAdmin from './LicenciasAdmin';
import AlertasAdmin from './AlertasAdmin';
import ExcepcionesAdmin from './ExcepcionesAdmin';
import OrgSettings from './OrgSettings';
import RulesSettings from './RulesSettings';
import AttendanceSettings from './AttendanceSettings';
import { IconTile } from './Icons';
import { hasFeature } from '../lib/plans';
import { adminSetEmployeeJustifiedPct } from '../lib/justify';
import { CertificateField } from './Certificate';
import { SearchBox, Chips, SelectFilter, DateRange, FilterBar, TotalsBar, fmtHM, matchesQuery, norm } from './Filters';
import { METHOD_LABEL, adminMarkAttendance, adminSetEmployeeLocations, adminEmployeeLocations, adminResetDevices, getAttendanceConfig, adminEmployeeOverrides } from '../lib/attendance';
import { methodsLine } from './EmployeeMethods';
import { uploadAvatar, signAvatars } from '../lib/avatars';
import { saveEmployeeDetails, DETAIL_FIELDS } from '../lib/employeeDetails';
import { visiblePoll } from '../lib/poll';
import { getPolicy, absenceDeduction, lateDeduction, justifiedPct, setHolidayDates } from '../lib/policy';
import { sendOrgMail, adminSetUserEmail } from '../lib/org';
import BulkImport from './BulkImport';
import { withLateness } from '../lib/manualRecord';
import CierreJornada from './CierreJornada';
import SectionHelp from './SectionHelp';
import RowMenu from './RowMenu';
import HomeTiles from './HomeTiles';
import AppStatusModal, { AppBadges } from './AppStatus';
import { listAppStatus } from '../lib/push';
import { HourlyProfileModal, HourlyConfigPanel, ConvocarModal, HourlySettlementsButton } from './HourlyPay';
import { getHourlyConfig, DEFAULT_AREAS, ratesFor } from '../lib/hourly';
import { AdminAdvances } from './Adelantos';
import ReportePagos from './ReportePagos';
import { openComprobante, payrollMail } from '../lib/comprobante';
import { sendReceiptMail } from '../lib/org';
import { listAdvances } from '../lib/advances';
import SedesAdmin from './SedesAdmin';

const DAYS=['Domingo','Lunes','Martes','Miércoles','Jueves','Viernes','Sábado'];
const DAYS_SHORT=['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'];
const fmtTime=iso=>iso?new Date(iso).toLocaleTimeString('es-AR',{hour:'2-digit',minute:'2-digit'}):'—';
const fmtDate=s=>s?new Date(s+'T12:00:00').toLocaleDateString('es-AR',{day:'2-digit',month:'2-digit',year:'numeric'}):'—';
const fmtMoney=n=>new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(n||0);

const Badge=({color,children})=>{
  const c={green:'bg-emerald-50 text-emerald-700 border-emerald-200',red:'bg-red-50 text-red-600 border-red-200',
    yellow:'bg-amber-50 text-amber-700 border-amber-200',blue:'bg-sky-50 text-sky-700 border-sky-200',
    gray:'bg-gray-50 text-gray-500 border-gray-200',purple:'bg-violet-50 text-violet-700 border-violet-200',
    orange:'bg-orange-50 text-orange-700 border-orange-200',teal:'bg-teal-50 text-teal-700 border-teal-200'};
  return <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-semibold border ${c[color]||c.gray}`}>{children}</span>;
};
const AV={urls:{}};
const Avatar=({initials,size='md',uid})=>{
  const colors=['bg-sky-100 text-sky-700','bg-emerald-100 text-emerald-700','bg-violet-100 text-violet-700','bg-amber-100 text-amber-700','bg-rose-100 text-rose-700'];
  const h=(initials||'?').charCodeAt(0)%colors.length;
  const sz=size==='sm'?'w-8 h-8 text-xs':size==='lg'?'w-12 h-12 text-base':'w-10 h-10 text-sm';
  const url=uid&&AV.urls[uid];
  if(url)return <img src={url} alt="" className={`${sz} rounded-2xl object-cover flex-shrink-0 bg-gray-100`}/>;
  return <div className={`${sz} rounded-2xl flex items-center justify-center font-bold flex-shrink-0 ${colors[h]}`}>{initials||'?'}</div>;
};
const Modal=({open,onClose,title,children,width='max-w-lg'})=>{
  if(!open)return null;
  return(
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose}/>
      <div className={`relative bg-white w-full ${width} rounded-t-3xl sm:rounded-3xl shadow-2xl max-h-[92vh] overflow-y-auto`}>
        <div className="flex items-center justify-between px-6 pt-6 pb-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>{title}</h2>
          <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-full text-gray-400 hover:bg-gray-100">✕</button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};
const Toast=({toast})=>{
  if(!toast)return null;
  const col=toast.type==='error'?'bg-red-500':toast.type==='warning'?'bg-amber-500':'bg-emerald-500';
  return <div className={`fixed top-4 left-1/2 -translate-x-1/2 z-[60] px-5 py-3 rounded-2xl text-sm font-semibold shadow-xl text-white max-w-sm text-center ${col}`}>{toast.msg}</div>;
};
const Input=({label,type='text',value,onChange,placeholder,min,step})=>(
  <div>
    {label&&<label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">{label}</label>}
    {type==='password'
      ? <PasswordInput value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
      : <input type={type} value={value} onChange={e=>onChange(e.target.value)} placeholder={placeholder} min={min} step={step||'any'}
      className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>}
  </div>
);
const ScheduleEditor=({schedule,onChange,locations=[]})=>(
  <div className="space-y-2">
    {DAYS.map((day,i)=>{
      const s=schedule[i]||{active:false,start_time:'09:00',end_time:'17:00'};
      const set=patch=>onChange({...schedule,[i]:{...s,...patch}});
      const inp="w-full min-w-0 text-sm px-2.5 py-2 border border-gray-200 rounded-xl focus:outline-none focus:ring-1 focus:ring-sky-400 bg-white";
      return(
        <div key={i} className={`rounded-2xl border px-3 py-2.5 ${s.active?'border-sky-100 bg-sky-50/30':'border-gray-100'}`}>
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={!!s.active} onChange={e=>set({active:e.target.checked})} className="w-4 h-4 text-sky-600 rounded-md flex-shrink-0"/>
            <span className="text-sm text-gray-800 font-semibold">{day}</span>
          </label>
          {s.active&&(
            <div className="mt-2.5 space-y-2 pl-7">
              <div className="flex items-center gap-2">
                <input type="time" value={s.start_time?.slice(0,5)||'09:00'} onChange={e=>set({start_time:e.target.value})} className={inp}/>
                <span className="text-gray-300">–</span>
                <input type="time" value={s.end_time?.slice(0,5)||'17:00'} onChange={e=>set({end_time:e.target.value})} className={inp}/>
              </div>
              {locations.length>1&&(
                <select value={s.location_id||''} onChange={e=>set({location_id:e.target.value||null})} className={inp} title="Sede de este día">
                  <option value="">📍 Sede habitual</option>
                  {locations.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
                </select>
              )}
            </div>
          )}
        </div>
      );
    })}
  </div>
);

// ─── EDIT RECORD MODAL ────────────────────────────────────────────────────────
function EditRecModal({rec,onSave,onClose,adminId,orgId}){
  const [cert,setCert]=useState(rec.certificate_path||null);
  const [status,setStatus]=useState(rec.status||'present');
  const [just,setJust]=useState(rec.justification||'');
  const [ci,setCi]=useState(rec.check_in?new Date(rec.check_in).toTimeString().slice(0,5):'');
  const [co,setCo]=useState(rec.check_out?new Date(rec.check_out).toTimeString().slice(0,5):'');
  const [reason,setReason]=useState('');
  const [saving,setSaving]=useState(false);
  const doSave=async()=>{
    setSaving(true);
    const patch={status,justification:just||null,certificate_path:(status==='justified'||status==='absent')?cert:null,
      check_in:ci?new Date(rec.date+'T'+ci+':00').toISOString():null,
      check_out:co?new Date(rec.date+'T'+co+':00').toISOString():null};
    if(patch.check_in&&patch.check_out)
      patch.minutes_worked=Math.round((new Date(patch.check_out)-new Date(patch.check_in))/60000);
    await onSave(rec.id,patch,reason||'Edición admin');
    setSaving(false);
  };
  return(
    <Modal open={true} onClose={onClose} title="Editar registro">
      <div className="space-y-5">
        <div className="bg-gray-50 rounded-2xl p-4 flex items-center gap-3">
          {rec.profiles&&<Avatar initials={rec.profiles.avatar} uid={rec.employee_id} size="sm"/>}
          <div><p className="font-bold text-gray-900 text-sm">{rec.profiles?.name||'Empleado'}</p><p className="text-xs text-gray-400">{fmtDate(rec.date)}</p></div>
        </div>
        <div>
          <label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Estado</label>
          <div className="grid grid-cols-2 gap-2">
            {[['present','✓ Presente','border-emerald-300 bg-emerald-50 text-emerald-700'],
              ['absent','✗ Ausente injust.','border-red-300 bg-red-50 text-red-600'],
              ['justified','~ Ausente justif.','border-amber-300 bg-amber-50 text-amber-700'],
              ['holiday','★ Feriado','border-violet-300 bg-violet-50 text-violet-700']].map(([v,l,cls])=>(
              <button key={v} onClick={()=>setStatus(v)} className={`py-2.5 rounded-2xl text-sm font-bold border-2 transition-all ${status===v?cls:'border-gray-200 text-gray-400 bg-white'}`}>{l}</button>
            ))}
          </div>
        </div>
        {(status==='absent'||status==='justified')&&(
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">
              {status==='justified'?'Motivo de justificación':'Motivo de la falta'}
            </label>
            <textarea value={just} onChange={e=>setJust(e.target.value)} rows={2}
              placeholder={status==='justified'?"Certificado médico, trámite...":"Falta sin aviso, etc."}
              className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 resize-none"/>
            {status==='justified'&&<div className="mt-3"><CertificateField orgId={orgId} employeeId={rec.employee_id} value={cert} onChange={setCert}/></div>}
          </div>
        )}
        <div className="grid grid-cols-2 gap-3">
          <Input label="Hora entrada" type="time" value={ci} onChange={setCi}/>
          <Input label="Hora salida" type="time" value={co} onChange={setCo}/>
        </div>
        <Input label="Motivo del cambio (auditoría)" value={reason} onChange={setReason} placeholder="Ej: Corrección autorizada"/>
        <button onClick={doSave} disabled={saving} className="w-full py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50" style={{background:'var(--brand-grad)'}}>
          {saving?'Guardando...':'Guardar cambios'}
        </button>
        <p className="text-xs text-gray-400 text-center">Este cambio quedará registrado en auditoría</p>
      </div>
    </Modal>
  );
}

// ─── ADD EMPLOYEE MODAL ───────────────────────────────────────────────────────
function AddEmployeeModal({onClose,onSave,areas=[],cfg={}}){
  const [payType,setPayType]=useState('fixed');const [area,setArea]=useState('');const [hRate,setHRate]=useState('');const [hDiem,setHDiem]=useState('');
  const [name,setName]=useState('');const [email,setEmail]=useState('');const [pw,setPw]=useState('');
  const [salary,setSalary]=useState('');const [hireDate,setHireDate]=useState('');const [sched,setSched]=useState({});
  const [det,setDet]=useState({});const [showDet,setShowDet]=useState(false);const [welcome,setWelcome]=useState(true);
  const [loading,setLoading]=useState(false);const [err,setErr]=useState('');
  const doSave=async()=>{
    if(!name||!email||!pw)return setErr('Completá nombre, email y contraseña');
    setLoading(true);setErr('');
    try{await onSave(name,email,pw,payType==='hourly'?0:(parseFloat(salary)||0),hireDate||null,payType==='hourly'?{}:sched,{payType,area,hRate,hDiem,details:det,welcome});onClose();}
    catch(e){setErr(e.message);}
    setLoading(false);
  };
  return(
    <Modal open={true} onClose={onClose} title="Agregar empleado">
      <div className="space-y-4">
        <Input label="Nombre completo" value={name} onChange={setName}/>
        <Input label="Email" type="email" value={email} onChange={setEmail}/>
        <Input label="Contraseña inicial" type="password" value={pw} onChange={setPw}/>
        <div className="grid grid-cols-2 gap-2">
          {[['fixed','Fijo'],['hourly','Por hora']].map(([v,l])=>(
            <button key={v} type="button" onClick={()=>setPayType(v)} className={`py-2.5 rounded-2xl border-2 text-sm font-bold ${payType===v?'border-sky-500 bg-sky-50 text-sky-800':'border-gray-100 text-gray-500'}`}>{l}</button>
          ))}
        </div>
        {areas.length>0&&<div><label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Perfil / área de trabajo</label>
          <select value={area} onChange={e=>setArea(e.target.value)} className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm bg-gray-50"><option value="">Sin área</option>{areas.map(a=><option key={a} value={a}>{a}</option>)}</select></div>}
        {payType==='fixed'
          ?<Input label="Sueldo base mensual (ARS)" type="number" value={salary} onChange={setSalary} placeholder="Ej: 500000"/>
          :<div className="grid grid-cols-2 gap-3">
            <Input label="Valor por hora" type="number" value={hRate} onChange={setHRate} placeholder={ratesFor(cfg,area).rate?`${area?'Perfil':'Predet.'}: ${ratesFor(cfg,area).rate}`:'Ej: 8500'}/>
            <Input label="Viático por día" type="number" value={hDiem} onChange={setHDiem} placeholder={ratesFor(cfg,area).perDiem?`${area?'Perfil':'Predet.'}: ${ratesFor(cfg,area).perDiem}`:'Ej: 5000'}/>
          </div>}
        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Fecha de alta</label>
          <input type="date" value={hireDate} onChange={e=>setHireDate(e.target.value)}
            className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
          <p className="text-xs text-gray-400 mt-1">Usada para calcular vacaciones según LCT</p>
        </div>
        {payType==='fixed'&&<div><label className="block text-xs font-bold text-gray-500 mb-2 uppercase tracking-wide">Horario semanal</label>
          <ScheduleEditor schedule={sched} onChange={setSched}/></div>}
        {payType==='hourly'&&<p className="text-xs text-gray-400">No necesita horario fijo: marca cuando viene y se le paga por los minutos trabajados. Podés convocarlo para un día puntual.</p>}
        <div>
          <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={welcome} onChange={e=>setWelcome(e.target.checked)}/>Enviar mail de bienvenida con el acceso</label>
          <button type="button" onClick={()=>setShowDet(v=>!v)} className="text-xs font-bold text-sky-700">{showDet?'− Ocultar datos personales':'+ Datos personales (opcional)'}</button>
          {showDet&&<div className="mt-2 grid grid-cols-1 gap-3">
            {DETAIL_FIELDS.map(([k,l,t])=><Input key={k} label={l} type={t||'text'} value={det[k]||''} onChange={v=>setDet(d=>({...d,[k]:v}))}/>)}
          </div>}
        </div>
        {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2.5 rounded-xl">{err}</p>}
        <button onClick={doSave} disabled={loading} className="w-full py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50" style={{background:'var(--brand-grad)'}}>
          {loading?'Creando...':'Agregar empleado'}
        </button>
      </div>
    </Modal>
  );
}

// ─── EXTRA HOURS MODAL ────────────────────────────────────────────────────────

// ─── NEW RECORD MODAL (carga manual admin) ────────────────────────────────────
function NewRecordModal({employees,defaultDate,onSave,onClose}){
  const today=localDateISO();
  const [empId,setEmpId]=useState(employees[0]?.id||'');
  const [date,setDate]=useState(defaultDate||today);
  const [ci,setCi]=useState('09:00');
  const [co,setCo]=useState('18:00');
  const [reason,setReason]=useState('');
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');

  const doSave=async()=>{
    if(!empId) return setErr('Elegí un empleado');
    if(!date)  return setErr('Ingresá la fecha');
    if(!ci)    return setErr('Ingresá la hora de entrada');
    if(co&&co===ci) return setErr('La salida no puede ser igual a la entrada');
    setSaving(true);setErr('');
    try{
      const checkInISO=new Date(date+'T'+ci+':00').toISOString();
      const outDay=new Date(date+'T'+co+':00'); if(co&&co<ci) outDay.setDate(outDay.getDate()+1);   // turno nocturno: la salida es al día siguiente
      const checkOutISO=co?outDay.toISOString():null;
      const minutesWorked=co?Math.round((new Date(checkOutISO)-new Date(checkInISO))/60000):null;
      await onSave({
        empId, date,
        check_in: checkInISO,
        check_out: checkOutISO,
        minutes_worked: minutesWorked,
        status: 'present',
        reason: reason||'Carga manual por admin',
      });
      onClose();
    }catch(e){setErr(e.message);}
    setSaving(false);
  };

  return(
    <Modal open={true} onClose={onClose} title="Agregar registro manual">
      <div className="space-y-4">
        <div className="bg-amber-50 border border-amber-100 rounded-2xl px-4 py-3 flex items-start gap-2">
          <span className="text-base flex-shrink-0">⚠️</span>
          <p className="text-xs text-amber-700">Este registro queda marcado como <strong>carga manual del admin</strong> y aparece en el historial de auditoría.</p>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Empleado</label>
          <select value={empId} onChange={e=>setEmpId(e.target.value)}
            className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50">
            {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>

        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Fecha</label>
          <input type="date" value={date} max={today} onChange={e=>setDate(e.target.value)}
            className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Entrada</label>
            <input type="time" value={ci} onChange={e=>setCi(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"/>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Salida (opcional)</label>
            <input type="time" value={co} onChange={e=>setCo(e.target.value)}
              className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"/>
          </div>
        </div>

        {ci&&co&&ci<co&&(
          <div className="bg-sky-50 rounded-2xl px-4 py-2.5 text-center">
            <p className="text-xs text-sky-600 font-bold">
              {(()=>{const mins=Math.round((new Date('2000-01-01T'+co)-new Date('2000-01-01T'+ci))/60000);return `${Math.floor(mins/60)}h ${mins%60}m trabajados`;})()}
            </p>
          </div>
        )}

        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Motivo de carga manual</label>
          <input value={reason} onChange={e=>setReason(e.target.value)}
            placeholder="Ej: No tenía celular, problema técnico..."
            className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
        </div>

        {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-xl">{err}</p>}

        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Cancelar</button>
          <button onClick={doSave} disabled={saving}
            className="py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50"
            style={{background:'var(--brand-grad)'}}>
            {saving?'Guardando...':'Guardar registro'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── PAYROLL MODAL ────────────────────────────────────────────────────────────// ─── EXTRA HOURS MODAL ────────────────────────────────────────────────────────

function PayrollModal({emp,month,stats,extraHours,pending=[],advances=[],empSchedMap,onClose,onSave}){
  const baseSalary=emp.salary||0;
  const deductPct=stats.scheduled>0?Math.round((stats.deductDays/stats.scheduled)*100*getPolicy().payroll.absence_factor):0;
  const deductAmt=absenceDeduction(baseSalary,stats.deductDays,stats.scheduled);
  const lateAmt=lateDeduction(stats.lateCount);
  // Use actual scheduled days from schedMap if available
  const schedDays=(()=>{
    if(!empSchedMap||!emp?.id)return stats.scheduled||20;
    const s=empSchedMap[emp.id]||{};
    const active=Object.values(s).filter(x=>x?.active).length;
    return active||stats.scheduled||20;
  })();
  // Prioridad: extra_hour_rate fijo > salary/monthly_hours > salary/días/8
  const hourlyRate = emp.extra_hour_rate > 0
    ? emp.extra_hour_rate
    : emp.monthly_hours > 0
      ? baseSalary / emp.monthly_hours
      : baseSalary / (schedDays * 8);
  const hourlyRateLabel = emp.extra_hour_rate > 0
    ? 'configurado'
    : emp.monthly_hours > 0
      ? `${baseSalary.toLocaleString('es-AR')} ÷ ${emp.monthly_hours}h`
      : `${baseSalary.toLocaleString('es-AR')} ÷ ${schedDays}días ÷ 8h`;
  const extraHrsTotal=extraHours.reduce((acc,h)=>acc+calcMonto(h,emp,schedDays),0);
  const [bonus,setBonus]=useState('0');
  const [notes,setNotes]=useState('');
  const [saving,setSaving]=useState(false);
  // Toggle descuento por minutos debidos
  const netDebt=Math.max(0,(stats.totalMissingMins||0)-(stats.totalExtraMins||0));
  const salaryPerMin=baseSalary/((stats.scheduled||20)*8*60);
  const minsDeductAmt=Math.round(netDebt*salaryPerMin);
  const [deductMins,setDeductMins]=useState(!!getPolicy().payroll.minutes_debt_deduct_default);
  const mH=m=>`${Math.floor(m/60)}h ${m%60}m`;
  const minsDeduct=deductMins?minsDeductAmt:0;
  const advTotal=advances.reduce((a,x)=>a+Number(x.amount||0),0);
  const totalNet=baseSalary-deductAmt-lateAmt-minsDeduct+Math.round(extraHrsTotal)+parseFloat(bonus||0)-advTotal;

  const doSave=async()=>{
    setSaving(true);
    await onSave({
      employee_id:emp.id, month,
      base_salary:baseSalary, days_scheduled:stats.scheduled, days_worked:stats.present,
      days_absent:stats.absent, days_justified:stats.justified,
      deduction_pct:deductPct, deduction_amt:deductAmt, late_deduct_amt:lateAmt,
      extra_hours_amt:Math.round(extraHrsTotal), advances_amt:advTotal, bonus:parseFloat(bonus||0),
      total_net:Math.round(totalNet), notes, status:'approved',
      mins_debt:netDebt, mins_deduct_applied:deductMins, mins_deduct_amt:minsDeduct,
    });
    setSaving(false);onClose();
  };

  const printRecibo=()=>{
    const MONTHS=['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
    const [y,m]=month.split('-').map(Number);
    const monthLabel=`${MONTHS[m-1]} ${y}`;
    const html=`<!DOCTYPE html><html lang="es"><head><meta charset="utf-8"/>
<title>Recibo — ${emp.name} — ${monthLabel}</title>
<style>
*{box-sizing:border-box;margin:0;padding:0;}
body{font-family:Arial,sans-serif;color:#1f2937;padding:2.5rem;max-width:720px;margin:0 auto;font-size:13px;}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #222d5a;padding-bottom:1.25rem;margin-bottom:1.5rem;}
.org{font-size:1.1rem;font-weight:900;color:#222d5a;}
.org-sub{font-size:.75rem;color:#6b7280;margin-top:.2rem;}
.title{text-align:right;}
.title h1{font-size:1rem;font-weight:900;color:#1f2937;text-transform:uppercase;letter-spacing:.05em;}
.title p{font-size:.75rem;color:#6b7280;margin-top:.2rem;}
.emp-box{background:#f8fafc;border-radius:8px;padding:1rem 1.25rem;margin-bottom:1.5rem;display:grid;grid-template-columns:1fr 1fr;gap:.5rem .75rem;}
.emp-box .label{font-size:.7rem;color:#6b7280;text-transform:uppercase;letter-spacing:.04em;}
.emp-box .value{font-size:.85rem;font-weight:700;color:#1f2937;}
.section{margin-bottom:1.25rem;}
.section-title{font-size:.7rem;font-weight:900;text-transform:uppercase;letter-spacing:.06em;color:#6b7280;margin-bottom:.6rem;padding-bottom:.4rem;border-bottom:1px solid #e5e7eb;}
.row{display:flex;justify-content:space-between;padding:.35rem 0;border-bottom:1px solid #f3f4f6;font-size:.82rem;}
.row:last-child{border-bottom:none;}
.row .lbl{color:#4b5563;}
.row .val{font-weight:700;}
.red{color:#dc2626;} .green{color:#059669;} .amber{color:#d97706;} .sky{color:#222d5a;}
.total-box{background:linear-gradient(135deg,#222d5a,#6366f1);border-radius:8px;padding:1rem 1.25rem;display:flex;justify-content:space-between;align-items:center;color:white;margin-top:1.5rem;}
.total-box .lbl{font-size:.75rem;font-weight:700;opacity:.85;text-transform:uppercase;letter-spacing:.05em;}
.total-box .val{font-size:1.5rem;font-weight:900;}
.notes-box{background:#fffbeb;border:1px solid #fde68a;border-radius:8px;padding:.75rem 1rem;margin-top:1rem;font-size:.8rem;color:#92400e;}
.footer{margin-top:2rem;padding-top:1rem;border-top:1px solid #e5e7eb;display:flex;justify-content:space-between;font-size:.7rem;color:#9ca3af;}
.sign-area{margin-top:3rem;display:grid;grid-template-columns:1fr 1fr;gap:2rem;}
.sign-line{border-top:1px solid #d1d5db;padding-top:.4rem;font-size:.7rem;color:#6b7280;text-align:center;}
@media print{body{padding:1rem;} .no-print{display:none;}}
</style></head><body>
<div class="header">
  <div>${BRAND.orgLogo?`<img src="${BRAND.orgLogo}" style="height:44px;margin-bottom:6px"/>`:''}<div class="org">${BRAND.orgName}</div><div class="org-sub">${BRAND.orgSub}</div></div>
  <div class="title"><h1>Recibo de Haberes</h1><p>${monthLabel} · Generado el ${new Date().toLocaleDateString('es-AR',{day:'2-digit',month:'long',year:'numeric'})}</p></div>
</div>

<div class="emp-box">
  <div><div class="label">Empleado</div><div class="value">${emp.name}</div></div>
  <div><div class="label">Período</div><div class="value">${monthLabel}</div></div>
  <div><div class="label">Días programados</div><div class="value">${stats.scheduled}</div></div>
  <div><div class="label">Días trabajados</div><div class="value">${stats.present}</div></div>
  <div><div class="label">Ausencias injustificadas</div><div class="value">${stats.absent}</div></div>
  <div><div class="label">Justificadas / Vacaciones</div><div class="value">${stats.justified}</div></div>
</div>

<div class="section">
  <div class="section-title">Haberes</div>
  <div class="row"><span class="lbl">Sueldo básico</span><span class="val sky">$${baseSalary.toLocaleString('es-AR')}</span></div>
  ${extraHours.length>0?extraHours.map(h=>`<div class="row"><span class="lbl">Hrs extra/remoto — ${h.date} (${h.hours}hs x${h.multiplier})</span><span class="val green">+$${calcMonto(h,emp,schedDays).toLocaleString('es-AR')}</span></div>`).join(''):''}
  ${advTotal>0?`<div class="row"><span class="lbl">Adelantos de sueldo</span><span class="val red">-$${advTotal.toLocaleString('es-AR')}</span></div>`:''}
  ${parseFloat(bonus||0)>0?`<div class="row"><span class="lbl">Bonus / adicional</span><span class="val green">+$${parseFloat(bonus||0).toLocaleString('es-AR')}</span></div>`:''}
</div>

<div class="section">
  <div class="section-title">Descuentos</div>
  ${deductAmt>0?`<div class="row"><span class="lbl">Por ausencias (${deductPct}% — ${+stats.deductDays.toFixed(2)} día${stats.deductDays!==1?'s':''})</span><span class="val red">-$${deductAmt.toLocaleString('es-AR')}</span></div>`:'<div class="row"><span class="lbl">Sin descuentos por faltas</span><span class="val green">✓</span></div>'}
  ${lateAmt>0?`<div class="row"><span class="lbl">Por llegadas tarde (${stats.lateCount})</span><span class="val red">-$${lateAmt.toLocaleString('es-AR')}</span></div>`:''}
  ${deductMins&&minsDeductAmt>0?`<div class="row"><span class="lbl">Por minutos debidos (${mH(netDebt)})</span><span class="val red">-$${minsDeductAmt.toLocaleString('es-AR')}</span></div>`:netDebt>0?`<div class="row"><span class="lbl">Minutos debidos (${mH(netDebt)}) — sin descuento aplicado</span><span class="val amber">⚠ ${mH(netDebt)} pendientes</span></div>`:''}
</div>

${netDebt>0?`<div class="section">
  <div class="section-title">Balance de horas</div>
  <div class="row"><span class="lbl">Minutos debidos (tardanzas sin recuperar)</span><span class="val amber">-${mH(netDebt)}</span></div>
  ${(stats.totalExtraMins||0)>0?`<div class="row"><span class="lbl">Minutos extra trabajados</span><span class="val green">+${mH(stats.totalExtraMins||0)}</span></div>`:''}
  <div class="row"><span class="lbl">Descuento aplicado</span><span class="val ${deductMins?'red':'green'}">${deductMins?`-$${minsDeductAmt.toLocaleString('es-AR')}`:'No aplicado por decisión del empleador'}</span></div>
</div>`:''}

<div class="total-box">
  <span class="lbl">Total neto a cobrar</span>
  <span class="val">$${Math.round(totalNet).toLocaleString('es-AR')}</span>
</div>

${notes?`<div class="notes-box">📝 ${notes}</div>`:''}

<div class="sign-area">
  <div class="sign-line">Firma empleado — ${emp.name}</div>
  <div class="sign-line">Firma empleador — ${BRAND.orgName}</div>
</div>

<div class="footer">
  <span>Recibo ${BRAND.hideProduct?'emitido por':'generado por '+BRAND.product+' —'} ${BRAND.orgName}</span>
  <span>Aclaración: este documento es informativo</span>
</div>
</body></html>`;
    const w=window.open('','_blank');
    w.document.write(html);
    w.document.close();
    setTimeout(()=>w.print(),500);
  };
  return(
    <Modal open={true} onClose={onClose} title={`Liquidación — ${emp.name}`}>
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-2xl p-4 space-y-2 text-sm">
          <div className="flex justify-between"><span className="text-gray-500">Sueldo base</span><span className="font-bold">{fmtMoney(baseSalary)}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Valor hora ({emp.extra_hour_rate>0?'fijo':emp.monthly_hours>0?`${emp.monthly_hours}h/mes`:'auto'})</span><span className="font-bold text-sky-600">{fmtMoney(Math.round(hourlyRate))}/h</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Días programados</span><span className="font-bold">{stats.scheduled}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Días trabajados</span><span className="font-bold text-emerald-600">{stats.present}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Ausentes injust.</span><span className="font-bold text-red-500">{stats.absent}</span></div>
          <div className="flex justify-between"><span className="text-gray-500">Ausentes justif.{stats.justifiedAbs>0&&stats.justifiedPct>0&&<span className="text-[11px] text-red-500"> (faltas justificadas descuentan {stats.justifiedPct}%)</span>}</span><span className="font-bold text-amber-600">{stats.justified}</span></div>
        </div>
        <div className="bg-red-50 rounded-2xl p-4 space-y-1 text-sm">
          <div className="flex justify-between"><span className="text-red-600">Descuento por faltas ({deductPct}%)</span><span className="font-bold text-red-600">-{fmtMoney(deductAmt)}</span></div>
          {lateAmt>0&&<div className="flex justify-between"><span className="text-red-600">Llegadas tarde ({stats.lateCount})</span><span className="font-bold text-red-600">-{fmtMoney(lateAmt)}</span></div>}
        </div>

        {advTotal>0&&(
          <div className="flex justify-between items-center bg-red-50 border border-red-100 rounded-2xl px-4 py-3">
            <span className="text-xs font-bold text-red-700">Adelantos de sueldo ({advances.length})</span>
            <span className="text-sm font-black text-red-600">-{fmtMoney(advTotal)}</span>
          </div>
        )}
        {pending.length>0&&(
          <div className="bg-amber-50 border border-amber-200 rounded-2xl px-4 py-3 text-xs text-amber-800">
            <p className="font-bold">⏳ {pending.length} hora{pending.length>1?'s':''} extra automática{pending.length>1?'s':''} sin aprobar ({pending.reduce((a,h)=>a+(h.hours||0),0).toFixed(1)}h)</p>
            <p className="mt-0.5">No están sumadas al sueldo. Aprobalas en la sección Hs Extra antes de liquidar.</p>
          </div>
        )}
        {/* Horas extra */}
        <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-50">
            <p className="text-xs font-bold text-gray-700">Horas extra / remoto</p>
            <p className="text-xs text-gray-400">{fmtMoney(Math.round(hourlyRate))}/h</p>
          </div>
          {extraHours.length===0?(
            <div className="px-4 py-4 text-center">
              <p className="text-xs text-gray-400">Sin horas extra este mes</p>
            </div>
          ):(
            <div className="divide-y divide-gray-50">
              {extraHours.map(h=>{
                const amt=calcMonto(h,emp,schedDays);
                return(
                  <div key={h.id} className="flex items-center justify-between px-4 py-2.5">
                    <div>
                      <p className="text-xs font-bold text-gray-800">
                        {h.hours}h {h.description?`— ${h.description}`:'extra'}
                        {h.multiplier!==1&&<span className="ml-1 text-amber-600 font-bold">×{h.multiplier}</span>}
                      </p>
                      <p className="text-xs text-gray-400">{h.date}</p>
                    </div>
                    <p className="text-sm font-black text-emerald-600">+{fmtMoney(amt)}</p>
                  </div>
                );
              })}
              <div className="flex justify-between items-center px-4 py-3 bg-emerald-50">
                <span className="text-xs font-bold text-emerald-700">Total horas extra</span>
                <span className="text-sm font-black text-emerald-700">+{fmtMoney(Math.round(extraHrsTotal))}</span>
              </div>
            </div>
          )}
        </div>

        {/* Balance de minutos + toggle descuento */}
        {(()=>{
          const isOk=netDebt<=0;
          return(
            <div className={`rounded-2xl p-4 space-y-2 text-sm border ${isOk?'bg-emerald-50 border-emerald-100':deductMins?'bg-red-50 border-red-100':'bg-amber-50 border-amber-100'}`}>
              <div className="flex items-center justify-between">
                <p className={`font-bold text-sm ${isOk?'text-emerald-700':deductMins?'text-red-700':'text-amber-700'}`}>⏱ Balance de horas</p>
                {isOk&&<span className="text-xs bg-emerald-100 text-emerald-700 px-2.5 py-1 rounded-xl font-bold">✅ Al día</span>}
              </div>
              {(stats.totalMissingMins||0)>0&&<div className="flex justify-between text-xs"><span className="text-gray-500">Minutos debidos</span><span className="font-bold text-amber-700">-{mH(stats.totalMissingMins||0)}</span></div>}
              {(stats.totalExtraMins||0)>0&&<div className="flex justify-between text-xs"><span className="text-gray-500">Minutos extra</span><span className="font-bold text-emerald-600">+{mH(stats.totalExtraMins||0)}</span></div>}
              <div className="flex justify-between text-xs font-bold pt-1 border-t border-gray-200">
                <span className="text-gray-600">Balance neto</span>
                <span className={isOk?'text-emerald-600':'text-amber-700'}>{isOk?(netDebt<0?`+${mH(Math.abs(netDebt))}`:'0 — recuperado'):`Debe ${mH(netDebt)}`}</span>
              </div>
              {!isOk&&(
                <div className="mt-2 pt-2 border-t border-amber-200">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xs font-bold text-gray-700">Descontar minutos debidos</p>
                      <p className="text-xs text-gray-400">{mH(netDebt)} → -{fmtMoney(minsDeductAmt)}</p>
                    </div>
                    <button onClick={()=>setDeductMins(d=>!d)}
                      className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${deductMins?'bg-red-500':'bg-gray-200'}`}>
                      <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${deductMins?'translate-x-5':'translate-x-0.5'}`}/>
                    </button>
                  </div>
                  {!deductMins&&<p className="text-xs text-amber-600 mt-1.5">Desactivado — no se descuenta de la liquidación. Quedará anotado en el recibo.</p>}
                  {deductMins&&<p className="text-xs text-red-600 mt-1.5">Activado — se descontarán {fmtMoney(minsDeductAmt)} del total neto.</p>}
                </div>
              )}
            </div>
          );
        })()}

        <div>
          <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Bonus / adicional (ARS)</label>
          <input type="number" value={bonus} onChange={e=>setBonus(e.target.value)} min="0"
            className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
        </div>
        <Input label="Notas" value={notes} onChange={setNotes} placeholder="Observaciones para la liquidación..."/>
        <div className="bg-sky-50 rounded-2xl p-4 flex justify-between items-center">
          <span className="font-bold text-sky-800 text-sm">TOTAL NETO</span>
          <span className="text-2xl font-black text-sky-700">{fmtMoney(Math.round(totalNet))}</span>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={printRecibo}
            className="py-3 rounded-2xl text-sm font-bold border-2 border-violet-200 text-violet-700 bg-violet-50 hover:bg-violet-100 flex items-center justify-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z"/></svg>
            Imprimir recibo
          </button>
          <button onClick={doSave} disabled={saving} className="py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50" style={{background:'var(--brand-grad)'}}>
            {saving?'Aprobando...':'Aprobar liquidación'}
          </button>
        </div>
      </div>
    </Modal>
  );
}


function SedeScopePicker({locations,value,onChange}){
  if(!locations||locations.length<2)return null;
  const all=value==null;
  const toggle=id=>{const cur=value||[];onChange(cur.includes(id)?cur.filter(x=>x!==id):[...cur,id]);};
  return(
    <div className="space-y-2">
      <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Sedes que puede ver</p>
      <div className="grid grid-cols-2 gap-2">
        <button type="button" onClick={()=>onChange(null)} className={`py-2.5 rounded-2xl border-2 text-sm font-bold ${all?'border-sky-500 bg-sky-50 text-sky-800':'border-gray-100 text-gray-500'}`}>Todas</button>
        <button type="button" onClick={()=>onChange(value||[locations[0].id])} className={`py-2.5 rounded-2xl border-2 text-sm font-bold ${!all?'border-sky-500 bg-sky-50 text-sky-800':'border-gray-100 text-gray-500'}`}>Solo algunas</button>
      </div>
      {!all&&(
        <div className="flex flex-wrap gap-2">
          {locations.map(l=>(
            <button key={l.id} type="button" onClick={()=>toggle(l.id)} className={`px-3 py-1.5 rounded-full text-xs font-bold border ${value.includes(l.id)?'bg-sky-600 text-white border-sky-600':'bg-white text-gray-500 border-gray-200'}`}>{l.name}</button>
          ))}
        </div>
      )}
      {!all&&<p className="text-[11px] text-gray-400">Solo ve y gestiona a las personas de esas sedes (y a quienes no tienen sede asignada).</p>}
    </div>
  );
}
function RolePicker({roles,value,onChange}){
  if(!roles.length)return null;
  return(
    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wide">Perfil de permisos
      <select value={value} onChange={e=>onChange(e.target.value)} className="mt-1 w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm bg-white font-normal normal-case">
        <option value="">Personalizado (elegir sección por sección)</option>
        {roles.map(r=><option key={r.id} value={r.id}>{r.name}</option>)}
      </select>
    </label>
  );
}
function RoleSummary({role}){
  if(!role)return null;
  const s=summarize(role.permissions);
  return(
    <div className="bg-sky-50 border border-sky-100 rounded-2xl px-4 py-3">
      <p className="text-sm font-bold text-sky-800">{role.name}</p>
      {role.description&&<p className="text-xs text-sky-700/80 mt-0.5">{role.description}</p>}
      <p className="text-xs text-sky-700 mt-1">{s.visible} secciones visibles · {s.editable} con edición. Si cambiás el perfil, este admin se actualiza solo.</p>
    </div>
  );
}

// ─── NEW ADMIN MODAL ──────────────────────────────────────────────────────────
function NewAdminModal({onClose,onSave,roles=[],locations=[]}){
  const [roleId,setRoleId]=useState('');
  const [locIds,setLocIds]=useState(null);
  const [name,setName]=useState('');
  const [email,setEmail]=useState('');
  const [pw,setPw]=useState('');
  const [perms,setPerms]=useState({});
  const [saving,setSaving]=useState(false);
  const [err,setErr]=useState('');
  const doSave=async()=>{
    if(!name||!email||!pw)return setErr('Completá nombre, email y contraseña');
    if(pw.length<6)return setErr('La contraseña debe tener al menos 6 caracteres');
    setSaving(true);setErr('');
    try{const r=roles.find(x=>x.id===roleId);await onSave(name,email,pw,r?materialize(r.permissions):materialize(perms),r?r.id:null,locIds);}
    catch(e){setErr(e.message);}
    setSaving(false);
  };
  return(
    <Modal open={true} onClose={onClose} title="Nuevo administrador" width="max-w-lg">
      <div className="space-y-4">
        <Input label="Nombre completo" value={name} onChange={setName} placeholder="Ej: Rivka Cohen"/>
        <Input label="Email" type="email" value={email} onChange={setEmail} placeholder="nombre@empresa.com"/>
        <Input label="Contraseña inicial" type="password" value={pw} onChange={setPw} placeholder="Mínimo 6 caracteres"/>
        <div className="border-t border-gray-100 pt-4 space-y-3">
          <SedeScopePicker locations={locations} value={locIds} onChange={setLocIds}/>
          <RolePicker roles={roles} value={roleId} onChange={setRoleId}/>
          {!roleId&&<AdminPermissionsEditor value={perms} onChange={setPerms}/>}
          {roleId&&<RoleSummary role={roles.find(x=>x.id===roleId)}/>}
        </div>
        {err&&<p className="text-red-500 text-xs bg-red-50 px-3 py-2 rounded-xl">{err}</p>}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Cancelar</button>
          <button onClick={doSave} disabled={saving} className="py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50"
            style={{background:'var(--brand-grad)'}}>
            {saving?'Creando...':'Crear admin'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── EDIT ADMIN PERMISSIONS MODAL ─────────────────────────────────────────────
function EditAdminPermsModal({admin,onClose,onSave,roles=[],locations=[]}){
  const [locIds,setLocIds]=useState(admin.admin_location_ids||null);
  const [roleId,setRoleId]=useState(admin.admin_role_id||'');
  const [perms,setPerms]=useState(admin.admin_permissions||{});
  const [isSuperAdmin,setIsSuperAdmin]=useState(!!admin.is_super_admin);
  const [saving,setSaving]=useState(false);
  const allPerms={};
  ALL_PERMISSIONS.forEach(p=>{allPerms[p.key]=true;});
  const doSave=async()=>{
    setSaving(true);
    const r=roles.find(x=>x.id===roleId);
    await onSave(isSuperAdmin?allPerms:(r?materialize(r.permissions):materialize(perms)),isSuperAdmin,isSuperAdmin?null:(r?r.id:null),isSuperAdmin?null:locIds);
    setSaving(false);
  };
  return(
    <Modal open={true} onClose={onClose} title={`Permisos — ${admin.name}`} width="max-w-lg">
      <div className="space-y-4">
        <div className="bg-gray-50 rounded-2xl p-4 flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-gray-900">Super admin</p>
            <p className="text-xs text-gray-400">Acceso completo a todo, igual que vos</p>
          </div>
          <button onClick={()=>setIsSuperAdmin(v=>!v)}
            className={`relative w-11 h-6 rounded-full transition-colors ${isSuperAdmin?'bg-violet-500':'bg-gray-200'}`}>
            <span className={`absolute top-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${isSuperAdmin?'translate-x-5':'translate-x-0.5'}`}/>
          </button>
        </div>
        {!isSuperAdmin&&(
          <div className="space-y-3">
            <SedeScopePicker locations={locations} value={locIds} onChange={setLocIds}/>
            <RolePicker roles={roles} value={roleId} onChange={setRoleId}/>
            {!roleId&&<AdminPermissionsEditor value={perms} onChange={setPerms}/>}
            {roleId&&<RoleSummary role={roles.find(x=>x.id===roleId)}/>}
          </div>
        )}
        {isSuperAdmin&&(
          <div className="bg-violet-50 border border-violet-100 rounded-2xl px-4 py-3">
            <p className="text-xs text-violet-700">Este admin tendrá acceso completo a todas las secciones y funciones.</p>
          </div>
        )}
        <div className="grid grid-cols-2 gap-2">
          <button onClick={onClose} className="py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Cancelar</button>
          <button onClick={doSave} disabled={saving} className="py-3 rounded-2xl text-sm font-bold text-white disabled:opacity-50"
            style={{background:'var(--brand-grad)'}}>
            {saving?'Guardando...':'Guardar permisos'}
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ─── MAIN ADMIN VIEW ──────────────────────────────────────────────────────────
// Qué función del plan habilita cada sección
const FEAT_OF={payroll:'payroll',vacaciones:'vacaciones',licencias:'licencias',tareas:'tareas',extra_hours:'horas_extra',analysis:'analisis',excepciones:'excepciones',alertas:'alertas',holidays:'feriados'};

export default function AdminView({profile,onLogout,orgInfo,onOrgChanged,onOpenPlatform}){
  const [tab,setTabRaw]=useState(()=>{try{return sessionStorage.getItem('g360_admin_tab')||'home'}catch(e){return 'home'}});
  const [badges,setBadges]=useState({});
  // El gesto/botón "atrás" del celular vuelve al inicio en vez de salir de la app
  const tabRef=useRef('home');
  tabRef.current=tab;
  const setTab=useCallback(t=>{
    if(t!=='home'&&tabRef.current==='home'){try{window.history.pushState({adminTab:t},'')}catch(e){}}
    tabRef.current=t;
    setTabRaw(t);
  },[]);
  useEffect(()=>{try{sessionStorage.setItem('g360_admin_tab',tab)}catch(e){}},[tab]);
  useEffect(()=>{if(tabRef.current!=='home'){try{window.history.pushState({adminTab:tabRef.current},'')}catch(e){}}},[]);
  useEffect(()=>{
    const onPop=()=>setTabRaw('home');
    window.addEventListener('popstate',onPop);
    return ()=>window.removeEventListener('popstate',onPop);
  },[]);
  useEffect(()=>{
    let alive=true;
    const load=async()=>{
      const cnt=async(t,f)=>{try{const {count}=await f(supabase.from(t).select('id',{count:'exact',head:true}));return count||0;}catch(e){return 0;}};
      const [v,l]=await Promise.all([
        cnt('vacation_requests',q=>q.eq('status','pending')),
        cnt('leave_requests',q=>q.eq('status','pending')),
      ]);
      if(alive)setBadges({vacaciones:v,licencias:l});
    };
    load();
    const stop=visiblePoll(load,120000);
    return ()=>{alive=false;stop()};
  },[tab]);
  // kept for compatibility
  const [allEmployees,setEmployees]=useState([]);
  const [sedeF,setSedeF]=useState('all');
  const [areaF,setAreaF]=useState('all');
  const [hourlyCfg,setHourlyCfg]=useState({default_rate:0,default_per_diem:0,areas:DEFAULT_AREAS,profiles:[]});
  const [adminRoles,setAdminRoles]=useState([]);
  const [avatarUrls,setAvatarUrls]=useState({});AV.urls=avatarUrls;
  const [attCfg,setAttCfg]=useState(null);const [attOv,setAttOv]=useState({});
  const [appStatus,setAppStatus]=useState({});const [showAppStatus,setShowAppStatus]=useState(false);
  const [hourlyEmp,setHourlyEmp]=useState(null);
  const [convocarEmp,setConvocarEmp]=useState(null);
  const [empLocMap,setEmpLocMap]=useState({});
  const [todayRecsAll,setTodayRecs]=useState([]);
  const [filteredRecs,setFilteredRecs]=useState([]);
  const [monthRecsAll,setMonthRecs]=useState([]);
  const [prevMonthRecs,setPrevMonthRecs]=useState([]);
  const [approvedVacations,setApprovedVacations]=useState([]);
  const [monthExceptions,setMonthExceptions]=useState([]);
  const [approvedLeaves,setApprovedLeaves]=useState([]);
  const [holidays,setHolidays]=useState([]);
  useEffect(()=>{setHolidayDates(holidays.map(h=>h.date));},[holidays]);
  const [locations,setLocations]=useState([]);
  const [toast,setToast]=useState(null);
  const [empSchedMap,setEmpSchedMap]=useState({});

  const [recFrom,setRecFrom]=useState(localDateISO());
  const [recTo,setRecTo]=useState(localDateISO());
  const [dashQ,setDashQ]=useState('');
  const [dashStatus,setDashStatus]=useState('all');
  const [empQ,setEmpQ]=useState('');
  const [empStatus,setEmpStatus]=useState('all');
  const [empLoc,setEmpLoc]=useState('all');
  const [payQ,setPayQ]=useState('');
  const [payStatus,setPayStatus]=useState('all');
  const [holQ,setHolQ]=useState('');
  const [holYear,setHolYear]=useState('all');
  const [holWhen,setHolWhen]=useState('all');
  const [recQ,setRecQ]=useState('');
  const [recStatus,setRecStatus]=useState('all');
  const [recMethod,setRecMethod]=useState('all');
  const [recLoc,setRecLoc]=useState('all');
  const [analysisEmp,setAnalysisEmp]=useState('all');
  const [filterEmp,setFilterEmp]=useState('all');
  const [analysisMonth,setAnalysisMonth]=useState(new Date().toISOString().slice(0,7));
  const [holForm,setHolForm]=useState({date:'',name:''});

  const [editRec,setEditRec]=useState(null);
  const [auditRecId,setAuditRecId]=useState(null);
  const [showNewRec,setShowNewRec]=useState(false);
  const [showNewAdmin,setShowNewAdmin]=useState(false);
  const [editAdminPerms,setEditAdminPerms]=useState(null);

  const [activeTab,setActiveTab]=useState('dashboard');
  const [showAddEmp,setShowAddEmp]=useState(false);
  const [showBulk,setShowBulk]=useState(false);
  const [showAddHol,setShowAddHol]=useState(false);
  const [editHol,setEditHol]=useState(null);

  const [editEmpId,setEditEmpId]=useState(null);
  const [editEmpSched,setEditEmpSched]=useState({});
  const [editEmpTol,setEditEmpTol]=useState('');
  const [editSalaryEmp,setEditSalaryEmp]=useState(null);
  const [editSalaryVal,setEditSalaryVal]=useState('');
  const [editExtraRateVal,setEditExtraRateVal]=useState('');
  const [editMonthlyHours,setEditMonthlyHours]=useState('');
  const [editEmpData,setEditEmpData]=useState(null); // {emp, form}
  const [cfgTab,setCfgTab]=useState('');
  const [viewEmpId,setViewEmpId]=useState(null);
  const [viewEmpMonth,setViewEmpMonth]=useState(new Date().toISOString().slice(0,7));
  const [viewEmpRecs,setViewEmpRecs]=useState([]);
  const [pendingExtras,setPendingExtras]=useState([]);
  const [advList,setAdvList]=useState([]);
  const loadAdv=useCallback(()=>{listAdvances({month:analysisMonth}).then(setAdvList).catch(()=>{});},[analysisMonth]);
  useEffect(()=>{loadAdv();},[loadAdv]);
  const [viewEmpExtra,setViewEmpExtra]=useState([]);
  const [payrollEmp,setPayrollEmp]=useState(null);
  const [extraHoursList,setExtraHoursList]=useState([]);
  const [payrolls,setPayrolls]=useState([]);

  // Filtro global por sede (solo si hay más de una)
  const SEDE_TABS=['dashboard','employees','records','payroll','analysis','vacaciones','licencias','tareas'];
  const sedeEff=SEDE_TABS.includes(tab)?sedeF:'all';
  const empSedes=e=>{const ids=empLocMap[e.id]||(e.location_id?[e.location_id]:[]);const sc=Object.values(empSchedMap[e.id]||{}).map(x=>x?.location_id).filter(Boolean);return [...new Set([...ids,...sc])];};
  const inSede=e=>{if(sedeEff==='all'||e.role!=='employee')return true;const ss=empSedes(e);return ss.length===0||ss.includes(sedeEff);};
  const areaEff=SEDE_TABS.includes(tab)?areaF:'all';
  const employees=(sedeEff==='all'&&areaEff==='all')?allEmployees:allEmployees.filter(e=>inSede(e)&&(areaEff==='all'||e.role!=='employee'||e.area===areaEff));
  const okEmpIds=new Set(employees.map(e=>e.id));
  const recInSede=r=>(areaEff==='all'||okEmpIds.has(r.employee_id))&&(sedeEff==='all'||(r.location_id?r.location_id===sedeEff:okEmpIds.has(r.employee_id)));
  const todayRecs=(sedeEff==='all'&&areaEff==='all')?todayRecsAll:todayRecsAll.filter(recInSede);
  const monthRecs=(sedeEff==='all'&&areaEff==='all')?monthRecsAll:monthRecsAll.filter(recInSede);
  const myScope=(!profile.is_super_admin&&Array.isArray(profile.admin_location_ids))?profile.admin_location_ids:null;
  const pickLocs=myScope?locations.filter(l=>myScope.includes(l.id)):locations;
  const sedePicker=(pickLocs.length>1&&SEDE_TABS.includes(tab))?(
    <select value={sedeF} onChange={e=>setSedeF(e.target.value)} className="min-w-0 flex-1 basis-[140px] px-3 py-2 rounded-xl border border-gray-200 text-[13px] bg-white font-medium text-gray-600 truncate" title="Filtrar por sede">
      <option value="all">{myScope?'Todas mis sedes':'Todas las sedes'}</option>
      {pickLocs.map(l=><option key={l.id} value={l.id}>{l.name}</option>)}
    </select>
  ):null;
  const areaOptions=[...new Set([...(hourlyCfg.areas||[]),...allEmployees.map(e=>e.area).filter(Boolean)])].filter(a=>allEmployees.some(e=>e.area===a));
  const areaPicker=(areaOptions.length>0&&SEDE_TABS.includes(tab))?(
    <select value={areaF} onChange={e=>setAreaF(e.target.value)} className="min-w-0 flex-1 basis-[140px] px-3 py-2 rounded-xl border border-gray-200 text-[13px] bg-white font-medium text-gray-600 truncate" title="Filtrar por área">
      <option value="all">Todas las áreas</option>
      {areaOptions.map(a=><option key={a} value={a}>{a}</option>)}
    </select>
  ):null;
  const pickers=<>{sedePicker}{areaPicker}</>;
  const showToast=(msg,type='success')=>{setToast({msg,type});setTimeout(()=>setToast(null),3500);};
  const todayDow=new Date().getDay();
  const can=(key)=>hasPermission(profile,key);
  const feat=(k)=>!orgInfo?.org?.features||hasFeature(orgInfo.org.features,k);

  const manualMark=async(emp,action)=>{
    const d=new Date();const def=String(d.getHours()).padStart(2,'0')+':'+String(d.getMinutes()).padStart(2,'0');
    const t=window.prompt(`Hora de ${action==='in'?'entrada':'salida'} de ${emp.name} (HH:MM)`,def);
    if(!t)return;
    const m=/^(\d{1,2}):(\d{2})$/.exec(t.trim());
    if(!m||Number(m[1])>23||Number(m[2])>59){showToast('Hora inválida. Usá el formato HH:MM','error');return;}
    const at=new Date();at.setHours(Number(m[1]),Number(m[2]),0,0);
    try{await adminMarkAttendance(emp.id,action,at.toISOString());showToast(`${action==='in'?'Entrada':'Salida'} registrada para ${emp.name}`);await loadAll();}
    catch(e){showToast(e.message||'No se pudo registrar','error');}
  };
  const loadAll=useCallback(async()=>{
    const [empsRes,schedsRes,todayRes,hqRes,holsRes]=await Promise.allSettled([
      getAllProfiles(),getAllSchedules(),getFilteredRecords({from:(()=>{const y=new Date();y.setDate(y.getDate()-1);return `${y.getFullYear()}-${String(y.getMonth()+1).padStart(2,'0')}-${String(y.getDate()).padStart(2,'0')}`;})(),to:localDateISO()}),getLocations(),getHolidays()
    ]);
    const emps=empsRes.status==='fulfilled'?empsRes.value:[];
    const scheds=schedsRes.status==='fulfilled'?schedsRes.value:[];
    const today=todayRes.status==='fulfilled'?todayRes.value:[];
    const hols=holsRes.status==='fulfilled'?holsRes.value:[];
    const locData=hqRes.status==='fulfilled'?hqRes.value:[];
    [empsRes,schedsRes,todayRes,hqRes,holsRes].forEach((r,i)=>{if(r.status==='rejected')console.error('loadAll['+i+']:',r.reason?.message);});
    setEmployees(emps||[]);setTodayRecs((today||[]).filter(r=>r.date===localDateISO()||(r.check_in&&!r.check_out&&Date.now()-new Date(r.check_in).getTime()<14*3600000)));setHolidays(hols||[]);
    setLocations(locData||[]);
    adminEmployeeLocations().then(setEmpLocMap).catch(()=>{});
    getHourlyConfig().then(setHourlyCfg).catch(()=>{});
    listAppStatus().then(setAppStatus).catch(()=>{});
    getAttendanceConfig().then(setAttCfg).catch(()=>{});
    adminEmployeeOverrides().then(setAttOv).catch(()=>{});
    listRoles().then(setAdminRoles).catch(()=>{});
    const sm={};(scheds||[]).forEach(s=>{if(!sm[s.employee_id])sm[s.employee_id]={};sm[s.employee_id][s.day_of_week]=s;});
    setEmpSchedMap(sm);
  },[]);

  useEffect(()=>{
    loadAll();
    runAlerts().catch(()=>{}); // Run alerts silently on load
    const stopA=visiblePoll(loadAll,90000);
    const stopB=visiblePoll(()=>{runAlerts().catch(()=>{});},300000);
    return ()=>{stopA();stopB();};
  },[loadAll]);

  useEffect(()=>{
    getFilteredRecords({from:recFrom||undefined,to:recTo||undefined,employeeId:filterEmp}).then(setFilteredRecs).catch(()=>{});
  },[recFrom,recTo,filterEmp]);

  useEffect(()=>{
    getRecordsByMonth(analysisMonth).then(setMonthRecs).catch(()=>{});
    // Load approved vacations that overlap this month
    const [y,m]=analysisMonth.split('-').map(Number);
    const monthStart=`${analysisMonth}-01`;
    const monthEnd=`${analysisMonth}-${String(new Date(y,m,0).getDate()).padStart(2,'0')}`;
    supabase.from('vacation_requests').select('employee_id,start_date,end_date')
      .eq('status','approved').lte('start_date',monthEnd).gte('end_date',monthStart)
      .then(({data})=>setApprovedVacations(data||[])).catch(()=>{});
    // Load schedule exceptions for the month
    supabase.from('schedule_exceptions').select('employee_id,date,type')
      .gte('date',monthStart).lte('date',monthEnd)
      .then(({data})=>setMonthExceptions(data||[])).catch(()=>{});
    // Load approved leave requests that overlap this month
    supabase.from('leave_requests').select('employee_id,start_date,end_date,type')
      .eq('status','approved').lte('start_date',monthEnd).gte('end_date',monthStart)
      .then(({data})=>setApprovedLeaves(data||[])).catch(()=>{});
    // Load previous month for trend comparison
    const prevDate=new Date(y,m-2,1);
    const prevMonth=prevDate.toISOString().slice(0,7);
    getRecordsByMonth(prevMonth).then(setPrevMonthRecs).catch(()=>{});

    // Load payrolls
    supabase.from('payroll').select('*').eq('month',analysisMonth)
      .then(({data})=>setPayrolls(data||[])).catch(()=>{});
    // Load extra hours for month (use explicit FK to avoid ambiguity)
    const [ey,em]=analysisMonth.split('-').map(Number);
    const eStart=`${analysisMonth}-01`;
    const eEnd=`${analysisMonth}-${String(new Date(ey,em,0).getDate()).padStart(2,'0')}`;
    supabase.from('extra_hours')
      .select('*, employee:profiles!extra_hours_employee_id_fkey(id,name,avatar)')
      .gte('date',eStart).lte('date',eEnd).eq('status','approved')
      .order('date',{ascending:false})
      .then(({data})=>attachCompensation(data,'employee')).then(d=>setExtraHoursList(d||[])).catch(()=>{});
    supabase.from('extra_hours').select('id,employee_id,hours').eq('status','pending').gte('date',eStart).lte('date',eEnd)
      .then(({data})=>setPendingExtras(data||[])).catch(()=>{});
  },[analysisMonth]);

  const todayIsoR=localDateISO();
  const dashTests={all:()=>true,present:r=>r?.check_in&&!r?.check_out,complete:r=>r?.check_in&&r?.check_out,late:r=>(r?.minutes_late||0)>0,absent:r=>!r?.check_in};
  const dashBase=employees.filter(e=>empSchedMap[e.id]?.[todayDow]?.active&&matchesQuery(dashQ,e.name));
  const dashRec=e=>todayRecs.find(r=>r.employee_id===e.id);
  const dashList=dashBase.filter(e=>dashTests[dashStatus](dashRec(e)));
  const dashCount=k=>dashBase.filter(e=>dashTests[k](dashRec(e))).length;
  const empTests={all:()=>true,bio:e=>e.bio_registered,nobio:e=>!e.bio_registered,salary:e=>e.salary>0,nosalary:e=>!(e.salary>0)};
  const empBase=employees.filter(e=>matchesQuery(empQ,e.name,e.email)&&(empLoc==='all'||e.location_id===empLoc));
  const empList=empBase.filter(empTests[empStatus]);
  const payBase=employees.filter(e=>e.pay_type!=='hourly'&&matchesQuery(payQ,e.name,e.email));
  const payList=payBase.filter(empTests[payStatus]);
  const holYears=[...new Set(holidays.map(h=>h.date.slice(0,4)))].sort();
  const holList=[...holidays].sort((a,b)=>a.date.localeCompare(b.date)).filter(h=>matchesQuery(holQ,h.name,fmtDate(h.date))&&(holYear==='all'||h.date.startsWith(holYear))&&(holWhen==='all'||(holWhen==='upcoming'?h.date>=todayIsoR:h.date<todayIsoR)));
  const recBase=filteredRecs.filter(r=>matchesQuery(recQ,r.profiles?.name,r.profiles?.email)&&(recMethod==='all'||r.check_in_method===recMethod)&&(recLoc==='all'||r.location_id===recLoc));
  const recTests={all:()=>true,present:r=>r.check_in&&!r.check_out,complete:r=>r.check_in&&r.check_out,late:r=>(r.minutes_late||0)>0,nocheckout:r=>r.check_in&&!r.check_out&&r.date<todayIsoR,absent:r=>r.status==='absent',justified:r=>r.status==='justified',device:r=>r.device_mismatch,edited:r=>r.edited_at||r.edited_by};
  const visibleRecs=recBase.filter(recTests[recStatus]);
  const recChipCount=k=>recBase.filter(recTests[k]).length;
  const recFiltersActive=!!(recQ||recStatus!=='all'||recMethod!=='all'||recLoc!=='all'||filterEmp!=='all'||recFrom!==todayIsoR||recTo!==todayIsoR);
  const clearRecFilters=()=>{setRecQ('');setRecStatus('all');setRecMethod('all');setRecLoc('all');setFilterEmp('all');setRecFrom(todayIsoR);setRecTo(todayIsoR);};
  const presentToday=todayRecs.filter(r=>r.check_in).length;
  const workingToday=employees.filter(e=>empSchedMap[e.id]?.[todayDow]?.active).length;

  // Add employee
  const handleAddEmployee=async(name,email,pw,salary,hireDate,sched,extra={})=>{
    if(!(await confirmEmailElsewhere(email)))throw new Error('No se creó la cuenta.');
    const {data:empId,error:rpcError}=await supabase.rpc('admin_create_employee',{p_name:name,p_email:email,p_password:pw});
    if(rpcError)throw new Error(rpcError.message);
    if(hireDate) await supabase.from('profiles').update({hire_date:hireDate}).eq('id',empId);
    if(!profile.is_super_admin&&Array.isArray(profile.admin_location_ids)&&profile.admin_location_ids.length>0){
      const {error:le}=await supabase.from('profiles').update({location_id:profile.admin_location_ids[0]}).eq('id',empId);
      if(le)throw new Error(le.message);
    }
    if(extra.details&&Object.values(extra.details).some(v=>v&&String(v).trim())){try{await saveEmployeeDetails(empId,extra.details);}catch(e){showToast('Empleado creado, pero no se guardaron los datos personales: '+e.message,'error');}}
    if(salary>0) await saveCompensation(empId,{salary});
    if(extra.payType==='hourly'||extra.area){
      const {error:pe}=await supabase.from('profiles').update({pay_type:extra.payType||'fixed',area:extra.area||null}).eq('id',empId);
      if(pe)throw new Error(pe.message);
    }
    if(extra.payType==='hourly') await saveCompensation(empId,{hourly_rate:extra.hRate===''?null:Number(extra.hRate),per_diem:extra.hDiem===''?null:Number(extra.hDiem)});
    const schedRows=Object.entries(sched).filter(([,s])=>s.active).map(([dow,s])=>({employee_id:empId,day_of_week:parseInt(dow),start_time:s.start_time||'09:00',end_time:s.end_time||'17:00',tolerance_minutes:null,active:true}));
    if(schedRows.length>0)await supabase.from('schedules').insert(schedRows);
    await loadAll();
    if(extra.welcome){try{await sendOrgMail('invite',empId);showToast('Empleado creado y mail de bienvenida enviado');}catch(e){showToast('Empleado creado, pero no se pudo enviar el mail: '+e.message,'error');}}
    else showToast('Empleado creado');
  };

  // Edit schedules
  const openEditSched=emp=>{setEditEmpId(emp.id);const sc=empSchedMap[emp.id]||{};setEditEmpSched(sc);const t=Object.values(sc).find(x=>x&&x.tolerance_minutes!=null);setEditEmpTol(t?String(t.tolerance_minutes):'');};
  const saveEmpSched=async()=>{
    try{
      const tolV=editEmpTol===''?null:Math.max(0,Math.min(120,parseInt(editEmpTol,10)||0));
      const withTol=Object.fromEntries(Object.entries(editEmpSched).map(([d,x])=>[d,{...x,tolerance_minutes:tolV}]));
      await upsertSchedules(editEmpId,withTol);
      await loadAll();setEditEmpId(null);
      showToast('Horario actualizado — aplica desde hoy');
    }
    catch(e){showToast(e.message,'error');}
  };

  // Edit salary
  const saveSalary=async()=>{
    const updates={salary:parseFloat(editSalaryVal)||0};
    if(editMonthlyHours!=='') updates.monthly_hours=parseFloat(editMonthlyHours)||null;
    if(editExtraRateVal!=='') updates.extra_hour_rate=parseFloat(editExtraRateVal)||null;
    try{await saveCompensation(editSalaryEmp.id,updates);}catch(e){showToast(e.message,'error');return;}
    setEmployees(p=>p.map(e=>e.id===editSalaryEmp.id?{...e,...updates}:e));
    setEditSalaryEmp(null);showToast('Sueldo actualizado');
  };

  // Edit employee data
  const openEditEmp = emp => {
    setEditEmpData({ emp, form: { name:emp.name, email:emp.email, password:'', avatar:emp.avatar||'', hire_date:emp.hire_date||'', jpct:emp.justified_deduct_pct==null?'':String(emp.justified_deduct_pct), location_ids:emp.location_id?[emp.location_id]:[] } });
    adminEmployeeLocations().then(m=>{
      const ids=m[emp.id]||(emp.location_id?[emp.location_id]:[]);
      setEditEmpData(p=>p&&p.emp.id===emp.id?{...p,form:{...p.form,location_ids:ids}}:p);
    }).catch(()=>{});
  };
  const saveEditEmp = async () => {
    const {emp, form} = editEmpData;
    const updates = { name:form.name,
      avatar: form.name.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase(),
      hire_date: form.hire_date || null };
    await supabase.from('profiles').update(updates).eq('id', emp.id);
    if ((form.email||'').trim().toLowerCase() !== (emp.email||'').toLowerCase()) {
      try { await adminSetUserEmail(emp.id, form.email); }
      catch(e){ showToast('No se pudo cambiar el correo de ingreso: '+(e.message||''),'error'); }
    }
    if (locations.length>1) {
      try { await adminSetEmployeeLocations(emp.id, form.location_ids||[]); }
      catch(e){ showToast('No se pudieron guardar las sedes: '+(e.message||''),'error'); }
    }
    if (hasPermission(profile,'salaries')) {
      try { await adminSetEmployeeJustifiedPct(emp.id, form.jpct); }
      catch(e){ showToast('No se pudo guardar la regla de faltas justificadas: '+(e.message||''),'error'); }
    }
    let pwError = null;
    if (form.password) {
      // Update password via admin RPC (supabase-js devuelve { error }, no lanza excepción)
      const { error } = await supabase.rpc('admin_update_password', { p_user_id: emp.id, p_password: form.password });
      pwError = error;
    }
    await loadAll();
    setEditEmpData(null);
    if (pwError) showToast('Datos guardados, pero no se pudo cambiar la contraseña: ' + pwError.message, 'error');
    else showToast('Empleado actualizado');
  };
  // Guarda toda la ficha de una persona (cuenta, trabajo, pago y datos personales)
  const saveFicha = async (emp, f) => {
    const errs = [];
    const upd = {
      name: f.name.trim(),
      avatar: f.name.trim().split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase(),
      hire_date: f.hire_date || null, pay_type: f.pay_type, area: f.area || null, active: !!f.active,
    };
    const {error:pe} = await supabase.from('profiles').update(upd).eq('id', emp.id);
    if (pe) throw new Error(pe.message);
    if ((f.email||'').trim().toLowerCase() !== (emp.email||'').toLowerCase()) {
      try { await adminSetUserEmail(emp.id, f.email.trim()); } catch(e){ errs.push('email: '+(e.message||'')); }
    }
    if (f.password) {
      const {error:pwe} = await supabase.rpc('admin_update_password', { p_user_id: emp.id, p_password: f.password });
      if (pwe) errs.push('contraseña: '+pwe.message);
    }
    if (locations.length>1) { try { await adminSetEmployeeLocations(emp.id, f.location_ids||[]); } catch(e){ errs.push('sedes: '+(e.message||'')); } }
    if (hasPermission(profile,'salaries_edit')) {
      const num = v => v===''||v==null?null:Number(v);
      try {
        await saveCompensation(emp.id, f.pay_type==='hourly'
          ? { hourly_rate:num(f.hourly_rate), per_diem:num(f.per_diem) }
          : { salary:num(f.salary)||0, monthly_hours:num(f.monthly_hours), extra_hour_rate:num(f.extra_hour_rate) });
      } catch(e){ errs.push('pago: '+(e.message||'')); }
      try { await adminSetEmployeeJustifiedPct(emp.id, f.jpct); } catch(e){ errs.push('falta justificada: '+(e.message||'')); }
    }
    try { await saveEmployeeDetails(emp.id, f.details||{}); } catch(e){ errs.push('datos personales: '+(e.message||'')); }
    await loadAll();
    if (errs.length) showToast('Guardado, pero falló: '+errs.join(' · '),'error'); else showToast('Ficha actualizada');
  };
  const handleDeleteEmp = async (emp) => {
    if (!window.confirm(`¿Eliminar a ${emp.name}? Se borrarán todos sus registros.`)) return;
    await supabase.from('profiles').update({active:false}).eq('id', emp.id);
    await loadAll();
    showToast('Empleado eliminado');
  };

  // View employee profile
  const openViewEmp = async (emp) => {
    setViewEmpId(emp.id);
    const [y,m] = viewEmpMonth.split('-').map(Number);
    const start = viewEmpMonth + '-01';
    const end = new Date(y,m,0).toISOString().split('T')[0];
    const [{data:recs},{data:extras}] = await Promise.all([
      supabase.from('attendance_records').select('*').eq('employee_id',emp.id).gte('date',start).lte('date',end).order('date'),
      supabase.from('extra_hours').select('*').eq('status','approved').eq('employee_id',emp.id).gte('date',start).lte('date',end)
    ]);
    setViewEmpRecs(recs||[]);
    setViewEmpExtra(extras||[]);
  };
  const reloadViewEmp = async (empId, month) => {
    const [y,m] = month.split('-').map(Number);
    const start = month + '-01';
    const end = new Date(y,m,0).toISOString().split('T')[0];
    const [{data:recs},{data:extras}] = await Promise.all([
      supabase.from('attendance_records').select('*').eq('employee_id',empId).gte('date',start).lte('date',end).order('date'),
      supabase.from('extra_hours').select('*').eq('status','approved').eq('employee_id',empId).gte('date',start).lte('date',end)
    ]);
    setViewEmpRecs(recs||[]);
    setViewEmpExtra(extras||[]);
  };

  // Records
  const handleSaveRec=async(recId,patch,reason)=>{
    try{await adminEditRecord(recId,patch,profile.id,reason);
      const updated=await getFilteredRecords({from:recFrom||undefined,to:recTo||undefined,employeeId:filterEmp});
      setFilteredRecs(updated);setEditRec(null);showToast('Registro actualizado');}
    catch(e){showToast(e.message,'error');}
  };
  const handleNewRec=async({empId,date,check_in,check_out,minutes_worked,status,reason})=>{
    // Check if record already exists for this employee+date
    const existing=filteredRecs.find(r=>r.employee_id===empId&&r.date===date);
    if(existing){
      // Update existing record
      await adminEditRecord(existing.id,{check_in,check_out,minutes_worked,status},profile.id,reason);
    } else {
      // Insert new record
      const {error}=await supabase.from('attendance_records').insert({
        employee_id:empId, date, check_in, check_out,
        minutes_worked, status, edited_by:profile.id,
        edited_at:new Date().toISOString(), edit_reason:reason,
      });
      if(error) throw error;
      // Log in attendance_edits
      await supabase.from('attendance_edits').insert({
        record_id:null, edited_by:profile.id,
        field_changed:'manual_entry', old_value:null,
        new_value:`${date} ${check_in?.slice(11,16)||''}-${check_out?.slice(11,16)||''}`,
        reason,
      }).catch(()=>{});
    }
    const updated=await getFilteredRecords({from:recFrom||undefined,to:recTo||undefined,employeeId:filterEmp});
    setFilteredRecs(updated);
    showToast('Registro guardado');
  };

  const handleManualRecord=async(empId,date,status,just,extra)=>{
    try{const ex2=await withLateness(empId,date,extra);await adminAddManualRecord({employeeId:empId,date,status,justification:just,adminId:profile.id,extra:ex2});
      const updated=await getFilteredRecords({from:recFrom||undefined,to:recTo||undefined,employeeId:filterEmp});
      setFilteredRecs(updated);showToast('Registro agregado');}
    catch(e){showToast(e.message,'error');}
  };

  // Extra hours


  const handleSaveExtra=async({id,empId,date,hours,description,multiplier,hourly_rate})=>{
    try{
      await saveExtraHour({id,empId,date,hours,description,multiplier,hourly_rate,approved_by:profile.id});
      await loadExtraHours(analysisMonth);
      setEditExtraData(null);
      showToast(id?'Registro actualizado':'Horas extra guardadas');
    }catch(e){showToast(e.message,'error');}
  };



  // Payroll
  const handleMarkPaid=async(prl,paid)=>{
    if(paid){
      const mine=advList.filter(x=>x.employee_id===prl.employee_id);
      const pend=mine.filter(x=>x.status==='pending');
      const counted=mine.filter(x=>x.status==='approved'||x.status==='paid').reduce((t,x)=>t+Number(x.amount||0),0);
      const money=n=>new Intl.NumberFormat('es-AR',{style:'currency',currency:'ARS',maximumFractionDigits:0}).format(n||0);
      const warns=[];
      if(pend.length)warns.push(`• Hay ${pend.length} adelanto${pend.length>1?'s':''} sin resolver (${money(pend.reduce((t,x)=>t+Number(x.amount||0),0))}). Aprobalos o rechazalos antes de pagar.`);
      if(Math.round(Number(prl.advances_amt||0))!==Math.round(counted))warns.push(`• La liquidación descuenta ${money(prl.advances_amt)} de adelantos, pero hoy suman ${money(counted)}. Volvé a guardar la liquidación para recalcular.`);
      if(warns.length&&!window.confirm('Antes de marcar como pagado:\n\n'+warns.join('\n')+'\n\n¿Marcar como pagado de todos modos?'))return;
    }
    const {error}=await supabase.from('payroll').update(paid?{status:'paid',paid_at:new Date().toISOString(),paid_by:profile.id}:{status:'approved',paid_at:null,paid_by:null}).eq('id',prl.id);
    if(error){showToast('No se pudo actualizar: '+error.message,'error');return;}
    const {data:p}=await supabase.from('payroll').select('*').eq('month',analysisMonth);
    setPayrolls(p||[]);showToast(paid?'Sueldo marcado como pagado':'Sueldo vuelto a aprobado');
  };
  const handleSavePayroll=async(data)=>{
    const prev=payrolls.find(x=>x.employee_id===data.employee_id);
    if(prev?.status==='paid')data={...data,status:'paid'};
    const {error:pe}=await supabase.from('payroll').upsert({...data,approved_by:profile.id},{onConflict:'employee_id,month'});
    if(pe){showToast('No se pudo guardar la liquidación: '+pe.message,'error');return;}
    const {data:p}=await supabase.from('payroll').select('*').eq('month',analysisMonth);
    setPayrolls(p||[]);showToast('Liquidación aprobada');
  };

  // Holidays
  const handleAddHol=async()=>{if(!holForm.date||!holForm.name)return;
    try{const h=await addHoliday(holForm.date,holForm.name,profile.id);setHolidays(p=>[...p,h]);setShowAddHol(false);setHolForm({date:'',name:''});showToast('Feriado agregado');}
    catch(e){showToast(e.message,'error');}
  };
  const handleImportHolidays=async(year)=>{
    const toImport=getHolidaysForYear(year);let count=0;
    for(const h of toImport){
      const {error}=await supabase.from('holidays').insert({date:h.date,name:h.name,created_by:profile.id}).select();
      if(!error)count++;
    }
    const hols=await getHolidays();setHolidays(hols);
    showToast(`Importados ${count} feriados de ${year}`);
  };
  const handleEditHol=async()=>{
    if(!editHol?.name||!editHol?.date) return;
    try{
      const{error}=await supabase.from('holidays').update({name:editHol.name,date:editHol.date}).eq('id',editHol.id);
      if(error)throw error;
      setHolidays(p=>p.map(h=>h.id===editHol.id?{...h,name:editHol.name,date:editHol.date}:h));
      setEditHol(null);
      showToast('Feriado actualizado');
    }catch(e){showToast(e.message,'error');}
  };
  const handleDelHol=async(id)=>{try{await deleteHoliday(id);setHolidays(p=>p.filter(h=>h.id!==id));}catch(e){showToast(e.message,'error');}};

  // Analysis stats
  const getStats=empId=>{
    const sched=empSchedMap[empId]||{};
    const [y,m]=analysisMonth.split('-').map(Number);const days=new Date(y,m,0).getDate();
    const today=new Date().toISOString().split('T')[0];
    let scheduled=0,present=0,absent=0,justified=0,justifiedAbs=0;
    let lateMins=0,lateCount=0;       // tardanzas
    let totalWorked=0;                 // minutos reales trabajados (check_out - check_in)
    let totalExpected=0;               // minutos esperados en días presentes
    let totalMissingMins=0;            // minutos faltantes acumulados (llegó tarde + salió antes)
    let totalExtraMins=0;              // minutos extra (salió tarde)

    for(let d=1;d<=days;d++){
      const date=`${analysisMonth}-${String(d).padStart(2,'0')}`;
      const dow=new Date(date+'T12:00:00').getDay();
      const daySched=sched[dow];
      if(!daySched?.active)continue;
      scheduled++;
      const expectedMinutes=(()=>{
        const [sh,sm]=(daySched.start_time||'09:00').split(':').map(Number);
        const [eh,em]=(daySched.end_time||'17:00').split(':').map(Number);
        const d=(eh*60+em)-(sh*60+sm);return d<=0?d+1440:d;   // turno nocturno
      })();

      const rec=monthRecs.find(r=>r.employee_id===empId&&r.date===date);
      if(rec?.check_in){
        present++;
        const late=rec.minutes_late||0; // ya tiene la tolerancia descontada (solo cuenta si >15min)
        if(late>0){lateMins+=late;lateCount++;}
        if(rec.minutes_worked){
          totalWorked+=rec.minutes_worked;
          totalExpected+=expectedMinutes;
          // diff = tiempo real trabajado vs jornada esperada
          // Si llegó 5min tarde (dentro de tolerancia) y salió 5min tarde → diff ≈ 0 → correcto
          // Si llegó 20min tarde (tardanza real 5min) y salió a la hora → diff = -5min
          // Si salió 30min tarde → diff = +30min de extra
          const diff=rec.minutes_worked-expectedMinutes;
          const mg=getPolicy().hours.diff_margin_minutes;
          if(diff>mg) totalExtraMins+=diff;       // margen configurable para evitar ruido GPS
          else if(diff<-mg) totalMissingMins+=Math.abs(diff);
        }
      } else if(rec?.status==='justified'){
        justified++;justifiedAbs++;   // falta justificada: puede descontar un % según las reglas
      } else if(approvedVacations.some(v=>v.employee_id===empId&&date>=v.start_date&&date<=v.end_date)){
        // Vacaciones aprobadas → justificado
        justified++;
      } else if(holidays.some(h=>h.date===date)){
        // Feriado → no cuenta como falta ni como día programado
        scheduled--; // lo quitamos del total porque no era día laboral
      } else if(monthExceptions.some(e=>e.employee_id===empId&&e.date===date&&e.type==='free')){
        // Excepción "No trabaja" → no cuenta como falta
        scheduled--; // tampoco era día laboral ese día
      } else if(approvedLeaves.some(l=>l.employee_id===empId&&date>=l.start_date&&date<=l.end_date)){
        // Licencia aprobada (enfermedad, duelo, etc.) → justificado
        justified++;
      } else if(date<=today){
        absent++;
        totalExpected+=expectedMinutes;
        totalMissingMins+=expectedMinutes; // día entero faltante
      }
    }
    return{
      scheduled,present,absent,justified,justifiedAbs,
      justifiedPct:justifiedPct(employees.find(e=>e.id===empId)),
      deductDays:absent+justifiedAbs*justifiedPct(employees.find(e=>e.id===empId))/100,
      lateMins,lateCount,
      totalWorked,totalExpected,
      totalExtraMins,totalMissingMins,
      pct:scheduled>0?Math.round(present/scheduled*100):0,
      punctPct:present>0?Math.round((present-lateCount)/present*100):100,
      lateHM: lateMins>0?`${Math.floor(lateMins/60)}h ${lateMins%60}m`:'0',
      totalWork: totalWorked,
    };
  };

  // Stats for previous month (for trend)
  const getPrevStats=empId=>{
    const sched=empSchedMap[empId]||{};
    const [y,m]=analysisMonth.split('-').map(Number);
    const prevDate=new Date(y,m-2,1);
    const prevMonth=prevDate.toISOString().slice(0,7);
    const days=new Date(prevDate.getFullYear(),prevDate.getMonth()+1,0).getDate();
    let scheduled=0,present=0;
    for(let d=1;d<=days;d++){
      const date=`${prevMonth}-${String(d).padStart(2,'0')}`;
      const dow=new Date(date+'T12:00:00').getDay();
      if(!sched[dow]?.active)continue;
      scheduled++;
      const rec=prevMonthRecs.find(r=>r.employee_id===empId&&r.date===date);
      if(rec?.check_in) present++;
    }
    return{scheduled,present,pct:scheduled>0?Math.round(present/scheduled*100):0};
  };

  const TABS=[
    ['dashboard','Panel','M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6','dashboard'],
    ['employees','Empleados','M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z','employees'],
    ['records','Registros','M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2','records'],
    ['payroll','Sueldos','M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z','salaries'],
    ['holidays','Feriados','M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z','holidays'],
    ['analysis','Análisis','M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z','analysis'],
    ['vacaciones','Vacaciones','M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 3v10.5','vacaciones'],
    ['tareas','Tareas','M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4','tareas'],
    ['licencias','Licencias','M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z','licencias'],
    ['excepciones','Excepciones','M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z','exceptions'],
    ['alertas','Alertas','M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9','notifications'],
    ['extra_hours','Hs Extra','M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z','extra_hours'],
    ['settings','Config','M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',null],
  ].filter(([id,label,path,perm])=>(!perm||can(perm))&&!(FEAT_OF[id]&&orgInfo?.org?.features&&!hasFeature(orgInfo.org.features,FEAT_OF[id])));

  useEffect(()=>{if(tab!=='home'&&orgInfo?.org&&!TABS.some(x=>x[0]===tab))setTabRaw('home');});
  const avKey=allEmployees.map(e=>e.id+':'+(e.avatar_path||'')).join('|');
  useEffect(()=>{const m={};allEmployees.forEach(e=>{if(e.avatar_path)m[e.id]=e.avatar_path;});signAvatars(m).then(setAvatarUrls).catch(()=>{});},[avKey]);// eslint-disable-line
  const TILE_LABEL={dashboard:'Hoy',settings:'Configuración'};

  return(
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Toast toast={toast}/>
      <div className="sticky top-0 z-10" style={{background:"linear-gradient(135deg,#0f172a 0%,#1e293b 100%)"}}>
        <div className="max-w-5xl mx-auto px-5 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={BRAND.orgLogo || logoMark} alt={BRAND.orgName} className="w-8 h-8 rounded-2xl flex-shrink-0 object-contain bg-white/5"/>
            <span className="text-white font-bold text-base tracking-tight">{BRAND.orgLogo ? BRAND.orgName : BRAND.product}</span>
          </div>
          <div className="flex items-center gap-2">
            {can('notifications')&&<NotificationBell/>}
            {orgInfo?.platform_admin&&onOpenPlatform&&<button onClick={onOpenPlatform} className="text-xs text-white/80 px-3 py-1.5 rounded-xl bg-white/10 hover:bg-white/20">Plataforma</button>}
            <button onClick={onLogout} className="text-xs text-white/60 px-3 py-1.5 rounded-xl hover:bg-white/10">Salir</button>
          </div>
        </div>
      </div>
      {tab!=='home'&&(
        <div className="bg-white border-b border-gray-100">
          <div className="max-w-5xl mx-auto px-4 py-2.5 flex items-center gap-3">
            <button onClick={()=>{try{window.history.back()}catch(e){setTabRaw('home')}}} className="flex items-center gap-1 text-xs font-bold text-sky-600 px-2.5 py-1.5 rounded-xl hover:bg-sky-50">
              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M15 19l-7-7 7-7"/></svg>Inicio
            </button>
            <span className="text-sm font-bold text-gray-800">{(()=>{const t=TABS.find(x=>x[0]===tab);return t?(TILE_LABEL[t[0]]||t[1]):'';})()}</span>
          </div>
        </div>
      )}

      <div className="flex-1 max-w-5xl mx-auto w-full px-5 py-6">

        <SectionHelp key={tab} tab={tab}/>

        {/* INICIO: casilleros de secciones */}
        {tab==='home'&&(
          <div className="space-y-5">
            <div>
              <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Hola, {(profile?.name||'').split(' ')[0]||'bienvenido/a'}</h2>
              <p className="text-sm text-gray-400">{new Date().toLocaleDateString('es-AR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
            </div>
            <div className="grid grid-cols-3 gap-3">
              {[{l:'Presentes',v:presentToday,c:'text-emerald-600',bg:'bg-emerald-50 border-emerald-100'},
                {l:'Ausentes',v:Math.max(0,workingToday-presentToday),c:'text-red-500',bg:'bg-red-50 border-red-100'},
                {l:'Con turno',v:workingToday,c:'text-sky-600',bg:'bg-sky-50 border-sky-100'}].map(({l,v,c,bg})=>(
                <button key={l} onClick={()=>setTab('dashboard')} className={`${bg} border rounded-3xl p-4 text-center active:scale-95 transition-transform`}>
                  <p className={`text-3xl font-black ${c}`}>{v}</p>
                  <p className="text-xs text-gray-500 mt-0.5 font-semibold">{l}</p>
                </button>
              ))}
            </div>
            <HomeTiles tabs={TABS} labels={TILE_LABEL} badges={badges} onOpen={setTab} userId={profile?.id}/>
          </div>
        )}

        {/* DASHBOARD */}
        {tab==='dashboard'&&(
          <div className="space-y-5">
            <div><h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Panel de hoy</h2>
              <p className="text-sm text-gray-400">{new Date().toLocaleDateString('es-AR',{weekday:'long',day:'numeric',month:'long',year:'numeric'})}</p>
            </div>
            {(sedePicker||areaPicker)&&<div className="flex gap-2 flex-wrap">{pickers}</div>}
            {can('records_edit')&&(
              <div className="flex items-center justify-between gap-3 rounded-3xl border border-gray-100 bg-white p-4">
                <div className="min-w-0"><p className="text-sm font-bold text-gray-900">Cierre de jornada</p><p className="text-xs text-gray-400">Fijá hasta qué hora se trabajó. Después, nadie puede sumar tiempo.</p></div>
                <CierreJornada employees={employees} locations={locations} showToast={showToast} onDone={loadAll}/>
              </div>
            )}
            <div className="grid grid-cols-3 gap-3">
              {[{l:'Presentes',v:presentToday,c:'text-emerald-600',bg:'bg-emerald-50 border-emerald-100'},
                {l:'Ausentes',v:Math.max(0,workingToday-presentToday),c:'text-red-500',bg:'bg-red-50 border-red-100'},
                {l:'Con turno',v:workingToday,c:'text-sky-600',bg:'bg-sky-50 border-sky-100'}].map(({l,v,c,bg})=>(
                <div key={l} className={`${bg} border rounded-3xl p-4 text-center`}>
                  <p className={`text-3xl font-black ${c}`}>{v}</p>
                  <p className="text-xs text-gray-500 mt-0.5 font-semibold">{l}</p>
                </div>
              ))}
            </div>
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100">
              <div className="px-5 py-4 border-b border-gray-50 space-y-3">
                <h3 className="font-bold text-gray-800 text-sm">Empleados hoy</h3>
                <div className="flex flex-wrap gap-2"><SearchBox value={dashQ} onChange={setDashQ} placeholder="Buscar empleado…"/>
                <Chips value={dashStatus} onChange={setDashStatus} options={[{id:'all',label:'Todos',count:dashBase.length},{id:'present',label:'Presentes',count:dashCount('present')},{id:'complete',label:'Jornada completa',count:dashCount('complete')},{id:'late',label:'Tarde',count:dashCount('late')},{id:'absent',label:'Ausentes',count:dashCount('absent')}]}/></div>
              </div>
              <div className="divide-y divide-gray-50">
                {dashList.map(emp=>{
                  const rec=todayRecs.find(r=>r.employee_id===emp.id);
                  const sc=empSchedMap[emp.id]?.[todayDow];
                  const mins=rec?.check_in&&rec?.check_out?Math.round((new Date(rec.check_out)-new Date(rec.check_in))/60000):null;
                  return(
                    <div key={emp.id} className="flex items-center gap-3 px-5 py-4">
                      <Avatar initials={emp.avatar} uid={emp.id} size="sm"/>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-bold text-gray-900 truncate">{emp.name}</p>
                        <p className="text-xs text-gray-400">{sc?.start_time?.slice(0,5)} – {sc?.end_time?.slice(0,5)}</p>
                      </div>
                      <div className="text-right">
                        {rec?.check_in?<>
                          <Badge color={rec.check_out?'teal':'green'}>{rec.check_out?'Jornada completa':'Presente'}</Badge>
                          <p className="text-xs text-gray-400 mt-0.5 font-mono">
                            {fmtTime(rec.check_in)}{rec.check_out?` → ${fmtTime(rec.check_out)}`:''}
                            {mins?` · ${Math.floor(mins/60)}h${mins%60}m`:''}
                          </p>
                          {(rec.minutes_late||0)>0&&<p className="text-xs text-amber-500">+{rec.minutes_late}min tarde</p>}
                        </>:<Badge color="red">Ausente</Badge>}
                        {hasPermission(profile,'records')&&!rec?.check_out&&(
                          <button onClick={()=>manualMark(emp,rec?.check_in?'out':'in')} className="mt-1 block ml-auto text-[11px] font-semibold text-sky-700 hover:underline">
                            {rec?.check_in?'Registrar salida':'Registrar entrada'}
                          </button>)}
                      </div>
                    </div>
                  );
                })}
                {workingToday===0&&<p className="text-sm text-gray-400 text-center py-8">Nadie tiene turno hoy</p>}
                {workingToday>0&&dashList.length===0&&<p className="text-sm text-gray-400 text-center py-8">Ningún empleado coincide con los filtros</p>}
              </div>
            </div>
          </div>
        )}

        {/* EMPLOYEES */}
        {tab==='employees'&&(
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <div><h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Empleados</h2>
                <p className="text-sm text-gray-400">{employees.length} registrados</p></div>
              <div className="flex gap-2">
                <button onClick={()=>setShowAppStatus(true)} className="px-3 py-2.5 rounded-2xl text-sm font-bold text-sky-700 bg-sky-50 border border-sky-100">App <span className="text-xs font-semibold">{Object.values(appStatus).filter(a=>a.installed).length}/{employees.length}</span></button>
                {can('employees_edit')&&feat('import_excel')&&<button onClick={()=>setShowBulk(true)} className="px-4 py-2.5 rounded-2xl text-sm font-bold border border-gray-200 text-gray-600 bg-white hover:bg-gray-50">Importar Excel</button>}
                <button onClick={()=>setShowAddEmp(true)} className="px-4 py-2.5 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>+ Agregar</button>
              </div>
            </div>
            <TotalsBar items={[
              {label:'Empleados',value:empList.length,tone:'sky'},
              {label:'Con huella',value:empList.filter(e=>e.bio_registered).length,tone:'green'},
              {label:'Sin huella',value:empList.filter(e=>!e.bio_registered).length,tone:empList.some(e=>!e.bio_registered)?'amber':'default'},
              hasPermission(profile,'salaries')?{label:'Sueldos base (mes)',value:fmtMoney(empList.reduce((a,e)=>a+(e.salary||0),0))}:null,
            ]}/>
            <FilterBar shown={empList.length} total={employees.length} noun="empleados" active={!!(empQ||empStatus!=='all'||empLoc!=='all')} onClear={()=>{setEmpQ('');setEmpStatus('all');setEmpLoc('all');}}>
              <div className="flex gap-2 flex-wrap">
                <SearchBox className="flex-1 min-w-[180px]" value={empQ} onChange={setEmpQ} placeholder="Buscar por nombre o email…"/>
                {pickers}
              </div>
              <Chips value={empStatus} onChange={setEmpStatus} options={[{id:'all',label:'Todos'},{id:'bio',label:'Con huella',count:empBase.filter(empTests.bio).length},{id:'nobio',label:'Sin huella',count:empBase.filter(empTests.nobio).length},{id:'salary',label:'Con sueldo cargado',count:empBase.filter(empTests.salary).length},{id:'nosalary',label:'Sin sueldo',count:empBase.filter(empTests.nosalary).length}]}/>
            </FilterBar>
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">
              {empList.length===0&&<p className="text-sm text-gray-400 text-center py-10">Ningún empleado coincide con los filtros</p>}
              {empList.map(emp=>(
                <div key={emp.id} className="p-3.5">
                  <div className="flex items-start gap-3">
                    <Avatar initials={emp.avatar} uid={emp.id}/>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-bold text-gray-900 text-sm">{emp.name}</p>
                        {emp.bio_registered?<Badge color="green">Bio ✓</Badge>:<Badge color="orange">Sin bio</Badge>}
                        {emp.pay_type==='hourly'
                          ?<Badge color="purple">Por hor{(()=>{const r=ratesFor(hourlyCfg,emp.area,emp).rate;return r>0?` · ${fmtMoney(r)}/h`:'';})()}</Badge>
                          :emp.salary>0&&<Badge color="teal">{fmtMoney(emp.salary)}/mes</Badge>}
                        {emp.area&&<Badge color="sky">{emp.area}</Badge>}
                        <AppBadges st={appStatus[emp.id]} Badge={Badge}/>
                      </div>
                      <p className="text-xs text-gray-400 truncate">{emp.email}</p>
                      {attCfg&&emp.pay_type!=='hourly'&&<p className="text-xs text-sky-700/80 mt-0.5">Marca: {methodsLine(attCfg,attOv[emp.id])}</p>}
                      {locations.length>1&&(()=>{const ss=empSedes(emp);return <p className="text-xs text-gray-500 mt-1">{ss.length?ss.map(id=>locations.find(l=>l.id===id)?.name).filter(Boolean).join(' · '):'Cualquier sede'}</p>;})()}
                                          </div>
                    <RowMenu items={[
                      {label:'Enviar invitación',onClick:async()=>{try{await sendOrgMail('invite',emp.id);showToast('Invitación enviada a '+emp.email);}catch(e){showToast(e.message||'No se pudo enviar','error');}}},
                      {label:'Eliminar',danger:true,onClick:()=>handleDeleteEmp(emp)},
                    ]}/>
                  </div>
                  <div className="flex gap-2 mt-2 pl-[52px] flex-wrap">
                    <button onClick={()=>openViewEmp(emp)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-violet-700 bg-violet-50 ">Ficha</button>
                    {emp.pay_type==='hourly'&&<button onClick={()=>setConvocarEmp(emp)} className="px-2.5 py-1 rounded-lg text-[11px] font-bold text-emerald-700 bg-emerald-50">Convocar</button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* RECORDS */}
        {tab==='records'&&(
          <div className="space-y-4">
            <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Registros</h2>
            <div className="flex gap-2 flex-wrap items-center justify-between">
              <p className="text-sm text-gray-400">Buscá por persona, fecha, estado o método de registro.</p>
              {can('records_edit')&&<CierreJornada employees={employees} locations={locations} showToast={showToast} onDone={loadAll}/>}
              {can('records_edit')&&<button onClick={()=>setShowNewRec(true)}
                className="flex items-center gap-2 px-4 py-2.5 rounded-2xl text-sm font-bold text-white flex-shrink-0"
                style={{background:'var(--brand-grad)'}}>
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
                Agregar registro
              </button>}
            </div>
            <TotalsBar items={[
              {label:'Registros',value:visibleRecs.length,tone:'sky'},
              {label:'Con entrada',value:visibleRecs.filter(r=>r.check_in).length,tone:'green'},
              {label:'Tardanzas',value:`${visibleRecs.filter(r=>(r.minutes_late||0)>0).length} · ${fmtHM(visibleRecs.reduce((a,r)=>a+(r.minutes_late||0),0))}`,tone:'amber'},
              {label:'Horas trabajadas',value:fmtHM(visibleRecs.reduce((a,r)=>a+(r.minutes_worked||0),0))},
              {label:'Ausentes',value:visibleRecs.filter(r=>r.status==='absent').length,tone:'red'},
              {label:'Justificadas',value:visibleRecs.filter(r=>r.status==='justified').length,tone:'amber'},
              {label:'Sin salida',value:visibleRecs.filter(r=>r.check_in&&!r.check_out).length},
            ]}/>
            <FilterBar shown={visibleRecs.length} total={filteredRecs.length} noun="registros" active={recFiltersActive} onClear={clearRecFilters}>
              <div className="flex gap-2 flex-wrap">
                <SearchBox className="flex-1 min-w-[180px]" value={recQ} onChange={setRecQ} placeholder="Buscar persona…"/>
                <SelectFilter value={filterEmp} onChange={setFilterEmp} options={employees.map(e=>({value:e.id,label:e.name}))} allLabel="Todas las personas"/>
                <SelectFilter value={recMethod} onChange={setRecMethod} options={Object.entries(METHOD_LABEL).map(([value,label])=>({value,label}))} allLabel="Todos los métodos"/>
                {pickers}
              </div>
              <DateRange from={recFrom} to={recTo} onChange={({from,to})=>{setRecFrom(from);setRecTo(to);}}/>
              <Chips value={recStatus} onChange={setRecStatus} options={[
                {id:'all',label:'Todos'},{id:'present',label:'Presentes',count:recChipCount('present')},{id:'complete',label:'Completos',count:recChipCount('complete')},
                {id:'late',label:'Tarde',count:recChipCount('late')},{id:'nocheckout',label:'Sin salida',count:recChipCount('nocheckout')},{id:'absent',label:'Ausentes',count:recChipCount('absent')},
                {id:'justified',label:'Justificados',count:recChipCount('justified')},{id:'device',label:'Otro dispositivo',count:recChipCount('device')},{id:'edited',label:'Editados',count:recChipCount('edited')}]}/>
            </FilterBar>
            {filteredRecs.length>=1000&&<p className="text-xs text-amber-600">Se muestran los primeros 1000 registros. Acotá las fechas para ver el resto.</p>}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">
              {visibleRecs.length===0&&<p className="text-sm text-gray-400 text-center py-10">{filteredRecs.length?'Ningún registro coincide con los filtros':'Sin registros'}</p>}
              {visibleRecs.map(rec=>{
                const mins=rec.check_in&&rec.check_out?Math.round((new Date(rec.check_out)-new Date(rec.check_in))/60000):null;
                return(
                  <div key={rec.id} className={`px-5 py-4 ${rec.device_mismatch?'bg-red-50/40':rec.edit_reason==='Carga manual por admin'||rec.edited_by?'bg-sky-50/30':''}`}>
                    <div className="flex items-start gap-3">
                      {rec.profiles&&<Avatar initials={rec.profiles.avatar} uid={rec.employee_id} size="sm"/>}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="text-sm font-bold text-gray-900">{rec.profiles?.name||'—'}</p>
                          <span className="text-xs text-gray-400">{fmtDate(rec.date)}</span>
                          {rec.edited_at&&<span className="text-xs text-amber-500">✏️</span>}
                        </div>
                        <div className="flex items-center gap-2 mt-0.5 flex-wrap text-xs text-gray-500">
                          {rec.check_in&&<span>↓ <span className="font-mono font-bold">{fmtTime(rec.check_in)}</span>{(rec.minutes_late||0)>0&&<span className="text-amber-500 ml-1">+{rec.minutes_late}min</span>}</span>}
                          {rec.check_in_method&&METHOD_LABEL[rec.check_in_method]&&<span className="text-gray-400">· {METHOD_LABEL[rec.check_in_method]}</span>}
                          {rec.device_mismatch&&<span className="text-red-500 font-semibold">· otro dispositivo</span>}
                          {rec.schedule_flag&&<span className="text-amber-600 font-semibold">· ⚠ {({non_working_day:'día no laboral',early:'entró muy temprano',after_hours:'entró fuera de horario',late_out:'salió muy tarde'})[rec.schedule_flag]||'fuera de horario'}</span>}
                          {rec.check_out&&<span>↑ <span className="font-mono font-bold">{fmtTime(rec.check_out)}</span></span>}
                          {mins&&<span className="text-gray-400">· {Math.floor(mins/60)}h{mins%60}m</span>}
                        </div>
                        {rec.justification&&<p className="text-xs text-amber-600 italic mt-0.5">"{rec.justification}"</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {rec.status==='present'&&<Badge color={rec.check_out?'teal':'green'}>{rec.check_out?'Completa':'Presente'}</Badge>}
                        {rec.status==='absent'&&<Badge color="red">Ausente</Badge>}
                        {rec.status==='justified'&&<Badge color="yellow">Justificada</Badge>}
                        {rec.status==='holiday'&&<Badge color="purple">Feriado</Badge>}
                        <button onClick={()=>setEditRec(rec)} className="p-2 text-gray-300 hover:text-sky-500 hover:bg-sky-50 rounded-xl transition-colors" title="Editar">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                        {can('records_edit')&&rec.edited_by&&(
                          <button onClick={()=>setAuditRecId(rec.id)} className="p-2 text-gray-300 hover:text-violet-500 hover:bg-violet-50 rounded-xl transition-colors" title="Ver historial de cambios">
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/></svg>
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <ManualForm employees={employees} onSave={handleManualRecord}/>
          </div>
        )}

        {/* PAYROLL */}
        {tab==='payroll'&&(
          <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div><h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Sueldos</h2>
                <p className="text-sm text-gray-400">Liquidación mensual</p></div>
              <div className="flex gap-2 flex-wrap w-full sm:w-auto [&>*]:min-w-0">
                {pickers}
                {feat('pago_hora')&&<HourlySettlementsButton employees={allEmployees} adminId={profile.id} orgName={BRAND.orgName||BRAND.product} showToast={showToast} canEdit={can('salaries_edit')} onChange={loadAdv}/>}
                <ReportePagos month={analysisMonth} employees={employees.filter(e=>e.role==='employee')} payrolls={payrolls} advances={advList} canExportPayroll={feat('export_liquidacion')} hourly={feat('pago_hora')}/>
                {feat('adelantos')&&<AdminAdvances employees={employees} month={analysisMonth} adminId={profile.id} showToast={showToast} onChange={loadAdv} canEdit={can('salaries_edit')}/>}
                <input type="month" value={analysisMonth} onChange={e=>setAnalysisMonth(e.target.value)} className="px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 flex-1 sm:flex-none min-w-[150px]"/>

              </div>
            </div>

            {(()=>{
              const rows=payList.map(emp=>{
                const st=getStats(emp.id);
                const prl=payrolls.find(p=>p.employee_id===emp.id);
                const sd=Object.values(empSchedMap?.[emp.id]||{}).filter(s=>s?.active).length||st.scheduled||20;
                const ded=absenceDeduction(emp.salary||0,st.deductDays,st.scheduled)+lateDeduction(st.lateCount);
                const ext=extraHoursList.filter(h=>h.employee_id===emp.id).reduce((a,h)=>a+calcMonto(h,h.employee||emp,sd),0);
                const adv=advList.filter(a=>a.employee_id===emp.id&&a.status!=='pending'&&a.status!=='rejected').reduce((x,a)=>x+Number(a.amount),0);
                return {base:emp.salary||0,ded,ext,adv,net:prl?.total_net||((emp.salary||0)-ded+ext-adv),approved:prl?.status==='approved'||prl?.status==='paid',abs:st.absent,late:st.lateCount};
              });
              const sum=k=>rows.reduce((a,r)=>a+r[k],0);
              return <TotalsBar items={[
                {label:'Sueldos base',value:fmtMoney(sum('base'))},
                {label:'Descuentos',value:`-${fmtMoney(sum('ded'))}`,tone:'red'},
                {label:'Horas extra',value:`+${fmtMoney(sum('ext'))}`,tone:'green'},
                {label:'Adelantos',value:`-${fmtMoney(sum('adv'))}`,tone:'red'},
                {label:'Total a pagar',value:fmtMoney(sum('net')),tone:'sky'},
                {label:'Liquidaciones aprobadas',value:`${rows.filter(r=>r.approved).length} de ${rows.length}`},
                {label:'Faltas sin justificar',value:sum('abs'),tone:'red'},
                {label:'Llegadas tarde',value:sum('late'),tone:'amber'},
              ]}/>;
            })()}
            <FilterBar shown={payList.length} total={employees.length} noun="empleados" active={!!(payQ||payStatus!=='all')} onClear={()=>{setPayQ('');setPayStatus('all');}}>
              <SearchBox value={payQ} onChange={setPayQ} placeholder="Buscar empleado…"/>
              <Chips value={payStatus} onChange={setPayStatus} options={[{id:'all',label:'Todos'},{id:'salary',label:'Con sueldo cargado',count:payBase.filter(empTests.salary).length},{id:'nosalary',label:'Sin sueldo cargado',count:payBase.filter(empTests.nosalary).length}]}/>
            </FilterBar>
            {/* Per-employee payroll cards */}
            <div className="grid gap-4 md:grid-cols-2">
              {payList.map(emp=>{
                const stats=getStats(emp.id);
                const prl=payrolls.find(p=>p.employee_id===emp.id);
                const empExtras=extraHoursList.filter(h=>h.employee_id===emp.id);
                const deductPct=stats.scheduled>0?Math.round((stats.deductDays/stats.scheduled)*100*getPolicy().payroll.absence_factor):0;
                const lateAmtEmp=lateDeduction(stats.lateCount);
                const deductAmt=absenceDeduction(emp.salary||0,stats.deductDays,stats.scheduled)+lateAmtEmp;
                const schedDaysEmp=Object.values(empSchedMap?.[emp.id]||{}).filter(s=>s?.active).length||stats.scheduled||20;
                const extraAmt=empExtras.reduce((acc,h)=>acc+calcMonto(h,h.employee||emp,schedDaysEmp),0);
                const estimated=(emp.salary||0)-deductAmt+extraAmt; // hourlyRateEmp already applied above
                return(
                  <div key={emp.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3"><Avatar initials={emp.avatar} uid={emp.id}/>
                        <div><p className="font-bold text-gray-900 text-sm">{emp.name}</p>
                          <p className="text-xs text-gray-400">Base: {fmtMoney(emp.salary||0)}/mes</p>
                          {emp.salary>0&&stats.scheduled>0&&(
                            <p className="text-xs text-gray-300">
                              Hs: {fmtMoney(emp.extra_hour_rate||Math.round((emp.salary||0)/(stats.scheduled*8)))}/h
                            </p>
                          )}
                        </div>
                      </div>
                      {prl?.status==='paid'?<Badge color="teal">💵 Pagado</Badge>:prl?.status==='approved'?<Badge color="green">✓ Aprobado</Badge>:<Badge color="gray">Borrador</Badge>}
                    </div>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-4">
                      <div className="bg-gray-50 rounded-xl p-2.5"><p className="text-gray-400">Trabajados</p><p className="font-bold text-gray-800">{stats.present}/{stats.scheduled} días</p></div>
                      <div className="bg-red-50 rounded-xl p-2.5"><p className="text-red-400">Descuento</p><p className="font-bold text-red-600">-{fmtMoney(deductAmt)} ({deductPct}%)</p></div>
                      <div className="bg-emerald-50 rounded-xl p-2.5"><p className="text-emerald-400">Hs extra</p><p className="font-bold text-emerald-600">+{fmtMoney(extraAmt)}</p></div>
                      <div className="bg-sky-50 rounded-xl p-2.5"><p className="text-sky-400">Estimado</p><p className="font-bold text-sky-700">{fmtMoney(prl?.total_net||estimated)}</p></div>
                    </div>
                    {prl?.notes&&<p className="text-xs text-gray-400 italic mb-3">"{prl.notes}"</p>}
                    <button onClick={()=>setPayrollEmp(emp)}
                      className="w-full py-2.5 rounded-2xl text-xs font-bold text-white" style={{background:'var(--brand-grad)'}}>
                      {prl?'Ver / editar liquidación':'Generar liquidación'}
                    </button>
                    {prl?.status==='paid'&&(
                      <button onClick={()=>openComprobante({orgName:BRAND.orgName||BRAND.product,empName:emp.name,payroll:prl,advances:advList.filter(a=>a.employee_id===emp.id&&a.status!=='pending'&&a.status!=='rejected')})}
                        className="w-full mt-2 py-2 rounded-2xl text-xs font-bold text-sky-700 bg-sky-50">Comprobante de pago{prl.receipt_no?` · ${prl.receipt_no}`:''}</button>
                    )}
                    {prl?.status==='paid'&&can('salaries_edit')&&emp.email&&(
                      <button onClick={async()=>{if(!window.confirm('¿Enviar el comprobante a '+emp.email+'?'))return;try{const m=payrollMail({orgName:BRAND.orgName||BRAND.product,empName:emp.name,payroll:prl});await sendReceiptMail(emp.id,m.subject,m.html);showToast('Comprobante enviado a '+emp.email);}catch(e){showToast(e.message||'No se pudo enviar','error');}}}
                        className="w-full mt-2 py-2 rounded-2xl text-xs font-bold text-gray-600 bg-gray-50">Enviar comprobante por mail</button>
                    )}
                    {prl&&can('salaries_edit')&&prl.status!=='draft'&&(
                      <button onClick={()=>handleMarkPaid(prl,prl.status!=='paid')}
                        className={`w-full mt-2 py-2 rounded-2xl text-xs font-bold ${prl.status==='paid'?'text-gray-400 bg-gray-50':'text-teal-700 bg-teal-50 border border-teal-100'}`}>
                        {prl.status==='paid'?'Marcar como no pagado':'Marcar como pagado'}
                      </button>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* HOLIDAYS */}
        {tab==='holidays'&&(
          <div className="space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Feriados</h2>
              <div className="flex gap-2 flex-wrap">
                {can('holidays_edit')&&[2025,2026,2027].map(y=>(
                  <button key={y} onClick={()=>handleImportHolidays(y)} className="px-3 py-2 rounded-2xl text-xs font-bold bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors">
                    Importar {y}
                  </button>
                ))}
                {can('holidays_edit')&&(<button onClick={()=>setShowAddHol(true)} className="px-4 py-2.5 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>+ Manual</button>)}
              </div>
            </div>
            <p className="text-xs text-gray-400">Los botones "Importar" cargan automáticamente todos los feriados argentinos + Iom Tov judíos del año.</p>
            {holidays.length>0&&(
              <>
              <TotalsBar items={[
                {label:'Feriados cargados',value:holidays.length,tone:'sky'},
                {label:'Próximos',value:holidays.filter(h=>h.date>=todayIsoR).length,tone:'green'},
                {label:'Ya pasaron',value:holidays.filter(h=>h.date<todayIsoR).length},
                {label:'En el listado',value:holList.length},
              ]}/>
              <FilterBar shown={holList.length} total={holidays.length} noun="feriados" active={!!(holQ||holYear!=='all'||holWhen!=='all')} onClear={()=>{setHolQ('');setHolYear('all');setHolWhen('all');}}>
                <SearchBox value={holQ} onChange={setHolQ} placeholder="Buscar feriado por nombre o fecha…"/>
                <div className="flex gap-3 flex-wrap">
                  <Chips value={holYear} onChange={setHolYear} options={[{id:'all',label:'Todos los años'},...holYears.map(y=>({id:y,label:y}))]}/>
                  <Chips value={holWhen} onChange={setHolWhen} options={[{id:'all',label:'Todos'},{id:'upcoming',label:'Próximos'},{id:'past',label:'Pasados'}]}/>
                </div>
              </FilterBar>
              </>
            )}
            <div className="bg-white rounded-3xl shadow-sm border border-gray-100 divide-y divide-gray-50">
              {holidays.length===0&&<p className="text-sm text-gray-400 text-center py-10">Sin feriados cargados — usá los botones de importar</p>}
              {holidays.length>0&&holList.length===0&&<p className="text-sm text-gray-400 text-center py-10">Ningún feriado coincide con los filtros</p>}
              {holList.map(h=>(
                <div key={h.id} className="flex items-center justify-between px-5 py-3.5">
                  <div>
                    <p className="font-bold text-gray-900 text-sm">{h.name}</p>
                    <p className="text-xs text-gray-400">{fmtDate(h.date)} — {DAYS[new Date(h.date+'T12:00:00').getDay()]}</p>
                  </div>
                  <div className="flex gap-1">
                    {can('holidays_edit')&&(<button onClick={()=>setEditHol({...h})} className="p-2 text-gray-300 hover:text-sky-500 hover:bg-sky-50 rounded-xl">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                    </button>)}
                    {can('holidays_edit')&&(<button onClick={()=>handleDelHol(h.id)} className="p-2 text-gray-300 hover:text-red-500 hover:bg-red-50 rounded-xl">
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/></svg>
                    </button>)}
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ANALYSIS */}
        {tab==='analysis'&&(
          <div className="space-y-5">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Análisis</h2>
              <div className="flex gap-2 items-center flex-wrap">
                {pickers}
                <select value={analysisEmp} onChange={e=>setAnalysisEmp(e.target.value)}
                  className="px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none bg-white">
                  <option value="all">Todos los empleados</option>
                  {employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
                </select>
                <input type="month" value={analysisMonth} onChange={e=>setAnalysisMonth(e.target.value)} className="px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"/>
                {can('export')&&<ExportButton employees={employees} records={monthRecs} schedMap={empSchedMap} month={analysisMonth} extraHours={extraHoursList} payrolls={payrolls}/>}
              </div>
            </div>

            {/* Calendar view for single employee */}
            {analysisEmp!=='all'&&(
              <MonthCalendar
                month={analysisMonth}
                records={monthRecs.filter(r=>r.employee_id===analysisEmp)}
                schedMap={empSchedMap[analysisEmp]||{}}
                holidays={holidays}
                onDayClick={cell=>{ const rec=monthRecs.find(r=>r.employee_id===analysisEmp&&r.date===cell.date); setEditRec(rec||{date:cell.date,employee_id:analysisEmp,profiles:{name:employees.find(e=>e.id===analysisEmp)?.name,avatar:employees.find(e=>e.id===analysisEmp)?.avatar}}); }}
              />
            )}

            {analysisEmp==='all'&&(()=>{
              const allCurrent=employees.map(e=>getStats(e.id));
              const allPrev=employees.map(e=>getPrevStats(e.id));
              const avgCurrent=allCurrent.length?Math.round(allCurrent.reduce((a,s)=>a+s.pct,0)/allCurrent.length):0;
              const avgPrev=allPrev.filter(p=>p.scheduled>0).length?Math.round(allPrev.filter(p=>p.scheduled>0).reduce((a,p)=>a+p.pct,0)/allPrev.filter(p=>p.scheduled>0).length):null;
              const totalPresent=allCurrent.reduce((a,s)=>a+s.present,0);
              const totalAbsent=allCurrent.reduce((a,s)=>a+s.absent,0);
              const totalLate=allCurrent.reduce((a,s)=>a+s.lateCount,0);
              const diff=avgPrev!==null?avgCurrent-avgPrev:null;
              return(
                <div className="bg-gradient-to-r from-sky-50 to-indigo-50 border border-sky-100 rounded-3xl p-5 mb-2">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-xs font-bold text-sky-600 uppercase tracking-wide">Resumen general del mes</p>
                    {diff!==null&&(
                      <span className={`text-xs font-bold px-2.5 py-1 rounded-xl ${diff>0?'bg-emerald-100 text-emerald-700':diff<0?'bg-red-100 text-red-600':'bg-gray-100 text-gray-500'}`}>
                        {diff>0?'↑':diff<0?'↓':'='} {diff!==0?`${Math.abs(diff)}pp`:'igual'} vs mes anterior
                      </span>
                    )}
                  </div>
                  <div className="grid grid-cols-4 gap-3 text-center">
                    {(()=>{
                      const avgPunct=allCurrent.filter(s=>s.present>0).length?Math.round(allCurrent.filter(s=>s.present>0).reduce((a,s)=>a+(s.punctPct||100),0)/allCurrent.filter(s=>s.present>0).length):100;
                      return[
                        {l:'Asist. promedio',v:`${avgCurrent}%`,c:'text-sky-700 text-xl'},
                        {l:'Puntualidad',v:`${avgPunct}%`,c:'text-violet-600 text-xl'},
                        {l:'Ausentes',v:totalAbsent,c:'text-red-500 text-xl'},
                        {l:'Tardanzas',v:totalLate,c:'text-amber-600 text-xl'},
                      ];
                    })().map(({l,v,c})=>(
                      <div key={l} className="bg-white/70 rounded-2xl p-2.5">
                        <p className={`font-black ${c}`}>{v}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{l}</p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            <div className="grid gap-4 md:grid-cols-2">
              {employees.filter(e=>analysisEmp==='all'||e.id===analysisEmp).map(emp=>{const s=getStats(emp.id);
                const pC=s.pct>=90?'text-emerald-600':s.pct>=75?'text-amber-600':'text-red-500';
                const bC=s.pct>=90?'from-emerald-400 to-emerald-500':s.pct>=75?'from-amber-400 to-amber-500':'from-red-400 to-red-500';
                const th=s.totalWork?`${Math.floor(s.totalWork/60)}h ${s.totalWork%60}m`:'—';
                const extraHrsEmp=extraHoursList.filter(h=>h.employee_id===emp.id);
                const totalExtraHrs=extraHrsEmp.reduce((a,h)=>a+h.hours,0);
                return(
                  <div key={emp.id} className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5">
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-center gap-3"><Avatar initials={emp.avatar} uid={emp.id}/>
                        <div><p className="font-bold text-gray-900 text-sm">{emp.name}</p><p className="text-xs text-gray-400">{s.scheduled} días prog.</p></div>
                      </div>
                      <div className="text-right">
                        <p className={`text-3xl font-black ${pC}`}>{s.pct}%</p>
                        {(()=>{const prev=getPrevStats(emp.id);const diff=s.pct-prev.pct;const hasPrev=prev.scheduled>0;
                          if(!hasPrev)return<p className="text-xs text-gray-300">primer mes</p>;
                          if(diff===0)return<p className="text-xs text-gray-400">= igual que mes ant.</p>;
                          return<p className={`text-xs font-bold ${diff>0?'text-emerald-500':'text-red-400'}`}>{diff>0?'↑':'↓'}{Math.abs(diff)}pp vs mes ant.</p>;
                        })()}
                      </div>
                    </div>
                    <div className="mb-1">
                      <div className="flex justify-between mb-1"><span className="text-xs text-gray-400">Asistencia</span><span className={`text-xs font-bold ${pC}`}>{s.pct}%</span></div>
                      <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                        <div className={`h-full rounded-full bg-gradient-to-r ${bC}`} style={{width:`${s.pct}%`}}/>
                      </div>
                    </div>
                    {(()=>{
                      const pp=s.punctPct;
                      const pPC=pp>=90?'text-violet-600':pp>=70?'text-amber-600':'text-red-500';
                      const pBC=pp>=90?'from-violet-400 to-violet-500':pp>=70?'from-amber-400 to-amber-500':'from-red-400 to-red-500';
                      return s.present>0?(
                        <div className="mb-4">
                          <div className="flex justify-between mb-1"><span className="text-xs text-gray-400">Puntualidad</span><span className={`text-xs font-bold ${pPC}`}>{pp}%</span></div>
                          <div className="w-full bg-gray-100 rounded-full h-2 overflow-hidden">
                            <div className={`h-full rounded-full bg-gradient-to-r ${pBC}`} style={{width:`${pp}%`}}/>
                          </div>
                        </div>
                      ):<div className="mb-4"/>;
                    })()}
                    <div className="grid grid-cols-4 gap-2 text-center mb-3">
                      {[{l:'Pres.',v:s.present,c:'text-emerald-600'},{l:'Aus.',v:s.absent,c:'text-red-500'},{l:'Just.',v:s.justified,c:'text-amber-600'},{l:'Tard.',v:s.lateCount,c:'text-violet-600'}].map(({l,v,c})=>(
                        <div key={l} className="bg-gray-50 rounded-2xl p-2.5"><p className={`text-xl font-black ${c}`}>{v}</p><p className="text-xs text-gray-400">{l}</p></div>
                      ))}
                    </div>
                    <div className="flex justify-between text-xs text-gray-400 bg-gray-50 rounded-2xl px-4 py-2.5">
                      <span>Horas: <span className="font-bold text-gray-700">{th}</span></span>
                      {totalExtraHrs>0&&<span>Extra/remoto: <span className="font-bold text-emerald-600">+{totalExtraHrs}hs</span></span>}
                      {s.lateCount>0&&<span>Tard: <span className="font-bold text-amber-600">{Math.round(s.lateMins/s.lateCount)}min</span></span>}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* VACACIONES */}
        {tab==='vacaciones'&&(
          <div className="space-y-4">
          {(sedePicker||areaPicker)&&<div className="flex gap-2 flex-wrap">{pickers}</div>}
          <VacacionesAdmin canEdit={can('vacaciones_edit')}
            employees={employees}
            adminId={profile.id}
            showToast={showToast}
          />
          </div>
        )}

        {/* TAREAS */}
        {tab==='tareas'&&(
          <div className="space-y-4">
          {(sedePicker||areaPicker)&&<div className="flex gap-2 flex-wrap">{pickers}</div>}
          <TareasAdmin employees={employees} adminId={profile.id} showToast={showToast} canEdit={can('tareas_edit')}/>
          </div>
        )}

        {/* LICENCIAS */}
        {tab==='licencias'&&(
          <div className="space-y-4">
          {(sedePicker||areaPicker)&&<div className="flex gap-2 flex-wrap">{pickers}</div>}
          <LicenciasAdmin employees={employees} adminId={profile.id} orgId={profile.organization_id} showToast={showToast} canEdit={can('licencias_edit')}/>
          </div>
        )}

        {/* EXCEPCIONES */}
        {tab==='excepciones'&&(
          <ExcepcionesAdmin employees={employees} adminId={profile.id} showToast={showToast} canEdit={can('exceptions_edit')}/>
        )}

        {/* ALERTAS */}
        {tab==='alertas'&&(
          <AlertasAdmin showToast={showToast} canEdit={can('notifications_edit')}/>
        )}

        {/* SETTINGS */}
        {tab==='extra_hours'&&(
          <HorasExtra canEdit={can('extra_hours_edit')}
            employees={employees}
            empSchedMap={empSchedMap}
            month={analysisMonth}
            embedded={false}
          />
        )}

        {tab==='settings'&&(
          <div className="space-y-5 max-w-2xl">
            <h2 className="text-2xl font-bold text-gray-900" style={{fontFamily:"'Playfair Display',serif"}}>Configuración</h2>
            {(()=>{
              const items=[
                {id:'org',label:'Mi organización',d:'Nombre, logo y datos'},
                ...(hasPermission(profile,'settings')?[{id:'rules',label:'Reglas de asistencia y sueldo',d:'Tolerancia, descuentos, faltas'},{id:'methods',label:'Formas de registrar',d:'Celular, QR, kiosco, horario habilitado para marcar y excepciones'}]:[]),
                {id:'sedes',label:'Sedes',d:'Lugares y ubicaciones'},
                ...(hasPermission(profile,'settings')?[{id:'hourly',label:'Perfiles por hora',d:'Categorías y valores'}]:[]),
                ...(profile.is_super_admin?[{id:'admins',label:'Administradores',d:'Accesos y permisos'},{id:'adminroles',label:'Perfiles de admin',d:'Permisos reutilizables'}]:[]),
              ];
              if(!cfgTab||!items.some(x=>x.id===cfgTab))return(
                <div className="grid grid-cols-2 gap-2.5">
                  {items.map(x=>(
                    <button key={x.id} onClick={()=>setCfgTab(x.id)} className="text-left bg-white rounded-2xl border border-gray-100 p-3.5 active:scale-[.98] transition-transform">
                      <p className="font-bold text-gray-800 text-[13px] leading-tight">{x.label}</p>
                      <p className="text-[11px] text-gray-400 mt-1 leading-snug">{x.d}</p>
                    </button>
                  ))}
                </div>
              );
              return <button onClick={()=>setCfgTab('')} className="text-xs font-bold text-sky-700 -mb-1">← Configuración · {items.find(x=>x.id===cfgTab)?.label}</button>;
            })()}

            {cfgTab==='org'&&<OrgSettings org={orgInfo?.org} onSaved={onOrgChanged} showToast={showToast}/>}

            {cfgTab==='rules'&&hasPermission(profile,'settings')&&<RulesSettings onSaved={onOrgChanged} showToast={showToast}/>}
            {cfgTab==='methods'&&hasPermission(profile,'settings')&&<AttendanceSettings features={orgInfo?.org?.features} showToast={showToast} canEmployees={hasPermission(profile,'employees_edit')}/>}

            {/* Admin management — only super admin */}
            {cfgTab==='admins'&&profile.is_super_admin&&(
              <div className="bg-white rounded-3xl shadow-sm border border-gray-100 p-5 space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm">Administradores</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Creá y gestioná accesos de admin con permisos específicos</p>
                  </div>
                  <button onClick={()=>setShowNewAdmin(true)}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl text-xs font-bold text-white flex-shrink-0"
                    style={{background:'var(--brand-grad)'}}>
                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4"/></svg>
                    Nuevo admin
                  </button>
                </div>
                <div className="space-y-2">
                  {employees.filter(e=>e.role==='admin'||e.id===profile.id).concat(
                    // Also show admins not in employees list
                  ).filter((e,i,arr)=>arr.findIndex(x=>x.id===e.id)===i).map(adm=>(
                    <div key={adm.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-2xl">
                      <Avatar initials={adm.avatar} uid={adm.id}/>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-bold text-gray-900 truncate">{adm.name}</p>
                          {adm.is_super_admin&&<span className="text-xs bg-violet-100 text-violet-700 px-2 py-0.5 rounded-lg font-bold">Super admin</span>}
                        </div>
                        <p className="text-xs text-gray-400 truncate">{adm.email}</p>
                        {!adm.is_super_admin&&adm.admin_permissions&&(
                          <p className="text-xs text-sky-600 mt-0.5">
                            {adminRoles.find(r=>r.id===adm.admin_role_id)?.name||'Personalizado'} · {summarize(adm.admin_permissions).visible} secciones, {summarize(adm.admin_permissions).editable} con edición{adm.admin_location_ids?` · ${adm.admin_location_ids.map(id=>locations.find(l=>l.id===id)?.name).filter(Boolean).join(', ')||'sin sedes'}`:''}
                          </p>
                        )}
                      </div>
                      {adm.id!==profile.id&&(
                        <button onClick={()=>setEditAdminPerms(adm)}
                          className="p-2 text-gray-400 hover:text-sky-600 hover:bg-sky-50 rounded-xl">
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/></svg>
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {cfgTab==='adminroles'&&profile.is_super_admin&&<AdminRoles roles={adminRoles} admins={allEmployees.filter(e=>e.role==='admin')} onChanged={async()=>{setAdminRoles(await listRoles());setEmployees((await getAllProfiles())||[]);}} showToast={showToast}/>}
            {cfgTab==='hourly'&&hasPermission(profile,'settings')&&<HourlyConfigPanel orgId={profile.organization_id} showToast={showToast}/>}
            {cfgTab==='sedes'&&<SedesAdmin locations={locations} org={orgInfo?.org} profile={profile} onChanged={loadAll} showToast={showToast}/>}
          </div>
        )}
      </div>

      {/* MODALS */}
      {viewEmpId&&<EmployeeProfileModal
        emp={employees.find(e=>e.id===viewEmpId)}
        month={viewEmpMonth}
        onMonthChange={async(m)=>{setViewEmpMonth(m);await reloadViewEmp(viewEmpId,m);}}
        records={viewEmpRecs}
        extraHours={viewEmpExtra}
        schedMap={empSchedMap[viewEmpId]||{}}
        holidays={holidays}
        adminId={profile.id}
        onEditRecord={async(recId,patch,reason)=>{
          await adminEditRecord(recId,patch,profile.id,reason||'Edición admin');
          await reloadViewEmp(viewEmpId,viewEmpMonth);
          showToast('Registro actualizado');
        }}
        onAddRecord={async(empId,date,status,just,extra)=>{
          await adminAddManualRecord({employeeId:empId,date,status,justification:just,adminId:profile.id,extra});
          await reloadViewEmp(viewEmpId,viewEmpMonth);
          showToast('Registro agregado');
        }}
        canEditMethods={can('employees_edit')}
        datosCtx={{canEdit:can('employees_edit'),locations,sedeIds:(()=>{const e=employees.find(x=>x.id===viewEmpId);return e?empSedes(e):[];})(),areas:hourlyCfg.areas||[],canPay:hasPermission(profile,'salaries'),canPayEdit:hasPermission(profile,'salaries_edit'),canJpct:hasPermission(profile,'salaries'),orgJpct:getPolicy().payroll.justified_deduct_pct||0}}
        onSaveDatos={f=>saveFicha(employees.find(x=>x.id===viewEmpId),f)}
        sedes={(()=>{const e=employees.find(x=>x.id===viewEmpId);return e?empSedes(e).map(id=>locations.find(l=>l.id===id)?.name).filter(Boolean):[];})()}
        app={appStatus[viewEmpId]}
        orgId={profile.organization_id}
        avatarUrl={avatarUrls[viewEmpId]}
        onPhoto={async f=>{try{await uploadAvatar(f,profile.organization_id,viewEmpId);await loadAll();showToast('Foto actualizada');}catch(e){showToast(e.message,'error');}}}
        onEditSched={()=>{const e=employees.find(x=>x.id===viewEmpId);if(e){setViewEmpId(null);openEditSched(e);}}}
        showToast={showToast}
        onMethodsChanged={()=>adminEmployeeOverrides().then(setAttOv).catch(()=>{})}
        onClose={()=>setViewEmpId(null)}
      />}
      {editEmpData&&(
        <Modal open={true} onClose={()=>setEditEmpData(null)} title="Editar empleado">
          <div className="space-y-4">
            <Input label="Nombre completo" value={editEmpData.form.name}
              onChange={v=>setEditEmpData(p=>({...p,form:{...p.form,name:v}}))}/>
            <Input label="Email" type="email" value={editEmpData.form.email}
              onChange={v=>setEditEmpData(p=>({...p,form:{...p.form,email:v}}))}/>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Nueva contraseña (opcional)</label>
              <PasswordInput value={editEmpData.form.password}
                onChange={e=>setEditEmpData(p=>({...p,form:{...p.form,password:e.target.value}}))}
                placeholder="Dejá vacío para no cambiar"
                className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-gray-50"/>
            </div>
            {locations.length>1&&(
              <div>
                <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">📍 Sedes donde puede marcar</label>
                <div className="rounded-2xl border border-gray-200 bg-gray-50 divide-y divide-gray-100">
                  {locations.map(l=>{
                    const sel=(editEmpData.form.location_ids||[]).includes(l.id);
                    return(
                      <label key={l.id} className="flex items-center gap-3 px-4 py-2.5 cursor-pointer">
                        <input type="checkbox" checked={sel} onChange={()=>setEditEmpData(p=>{
                          const cur=p.form.location_ids||[];
                          return {...p,form:{...p.form,location_ids:sel?cur.filter(x=>x!==l.id):[...cur,l.id]}};
                        })}/>
                        <span className="text-sm text-gray-800">{l.name}</span>
                      </label>);
                  })}
                </div>
                <p className="text-xs text-gray-400 mt-1">{(editEmpData.form.location_ids||[]).length===0?'Sin ninguna marcada puede marcar en cualquier sede.':'Solo podrá marcar en las sedes tildadas (la más cercana a donde esté).'}</p>
              </div>
            )}
            {hasPermission(profile,'salaries')&&(
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4">
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Faltas justificadas y sueldo</p>
                <p className="text-xs text-gray-400 mt-0.5">Qué porcentaje del día se descuenta cuando una falta de este empleado queda justificada. Vacío = lo que define la organización ({getPolicy().payroll.justified_deduct_pct||0}%).</p>
                <div className="flex items-center gap-2 mt-2">
                  <input inputMode="decimal" value={editEmpData.form.jpct} placeholder={`Según la organización (${getPolicy().payroll.justified_deduct_pct||0}%)`}
                    onChange={e=>setEditEmpData(p=>({...p,form:{...p.form,jpct:e.target.value.replace(/[^0-9.]/g,'')}}))}
                    className="flex-1 px-3.5 py-2.5 rounded-2xl border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-sky-400"/>
                  <span className="text-sm text-gray-500">%</span>
                </div>
              </div>
            )}
            <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-bold text-gray-500 uppercase tracking-wide">Dispositivo habitual</p>
                <p className="text-xs text-gray-400 mt-0.5">El primero desde el que marca queda como habitual. Si cambia de celular, restablecelo.</p>
              </div>
              <button type="button" onClick={async()=>{
                if(!window.confirm('¿Restablecer el dispositivo habitual? El próximo celular desde el que marque pasará a ser el habitual.'))return;
                try{await adminResetDevices(editEmpData.emp.id);showToast('Dispositivo habitual restablecido');}catch(e){showToast(e.message||'No se pudo restablecer','error');}
              }} className="shrink-0 text-xs font-semibold text-sky-700 bg-sky-100 px-3 py-2 rounded-xl">Restablecer</button>
            </div>
            <div className="bg-sky-50 border border-sky-100 rounded-2xl p-4">
              <label className="block text-xs font-bold text-sky-700 mb-1.5 uppercase tracking-wide">📅 Fecha de alta / ingreso</label>
              <input type="date" value={editEmpData.form.hire_date||''}
                onChange={e=>setEditEmpData(p=>({...p,form:{...p.form,hire_date:e.target.value}}))}
                className="w-full px-4 py-2.5 rounded-2xl border border-sky-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400 bg-white"/>
              <p className="text-xs text-sky-600 mt-1.5">Usada para calcular vacaciones y antigüedad según LCT</p>
              {editEmpData.form.hire_date&&(()=>{
                const hire=new Date(editEmpData.form.hire_date),now=new Date();
                const months=(now.getFullYear()-hire.getFullYear())*12+(now.getMonth()-hire.getMonth());
                const years=Math.floor(months/12);
                const days=months<6?Math.min(Math.floor((now-hire)/(1000*60*60*24*20)),14):years<5?14:years<10?21:years<20?28:35;
                const label=months<6?`${months} meses`:years===0?`${months} meses`:`${years} año${years!==1?'s':''}`;
                return <p className="text-xs font-bold text-sky-700 mt-1">→ {label} · {days} días de vacaciones</p>;
              })()}
            </div>
            <div className="flex gap-3 pt-2">
              <button onClick={()=>setEditEmpData(null)}
                className="flex-1 py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500 hover:bg-gray-50">
                Cancelar
              </button>
              <button onClick={saveEditEmp}
                className="flex-1 py-3 rounded-2xl text-sm font-bold text-white"
                style={{background:'var(--brand-grad)'}}>
                Guardar cambios
              </button>
            </div>
          </div>
        </Modal>
      )}
      {showAppStatus&&<AppStatusModal employees={employees} status={appStatus} onClose={()=>setShowAppStatus(false)}/>}
      {hourlyEmp&&<HourlyProfileModal emp={hourlyEmp} cfg={hourlyCfg} onClose={()=>setHourlyEmp(null)} onSaved={loadAll} showToast={showToast}/>}
      {convocarEmp&&<ConvocarModal emp={convocarEmp} adminId={profile.id} onClose={()=>setConvocarEmp(null)} showToast={showToast}/>}
      {showBulk&&<BulkImport locations={locations} areas={hourlyCfg.areas||[]} existingEmails={allEmployees.map(e=>e.email)} onClose={()=>setShowBulk(false)} onDone={loadAll}/>}
      {showAddEmp&&<AddEmployeeModal areas={hourlyCfg.areas} cfg={hourlyCfg} onClose={()=>setShowAddEmp(false)} onSave={handleAddEmployee}/>}
      {editEmpId&&(
        <Modal open={true} onClose={()=>setEditEmpId(null)} title="Editar horario">
          <div className="space-y-4">
            <ScheduleEditor schedule={editEmpSched} onChange={setEditEmpSched} locations={locations}/>
            <div>
              <label className="block text-xs font-semibold text-gray-500">Tolerancia de este empleado (minutos)</label>
              <input type="number" min="0" max="120" value={editEmpTol} onChange={e=>setEditEmpTol(e.target.value)} placeholder={`Vacío = regla de la organización (${getPolicy().lateness.tolerance_minutes} min)`} className="mt-1 w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm bg-white"/>
              <p className="text-[11px] text-gray-400 mt-1">Dejalo vacío para usar la regla general. 0 = sin tolerancia.</p>
            </div>
            <button onClick={saveEmpSched} className="w-full py-3 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>Guardar horario</button>
          </div>
        </Modal>
      )}
      {editSalaryEmp&&(
        <Modal open={true} onClose={()=>setEditSalaryEmp(null)} title={`Sueldo — ${editSalaryEmp.name}`}>
          <div className="space-y-4">
            <Input label="Sueldo base mensual (ARS)" type="number" value={editSalaryVal} onChange={setEditSalaryVal} placeholder="Ej: 500000"/>
            <div className="space-y-2">
              <Input label="Horas mensuales" type="number" value={editMonthlyHours} onChange={setEditMonthlyHours} placeholder="Ej: 160, 180, 200"/>
              <p className="text-xs text-gray-400">
                {parseFloat(editMonthlyHours)>0&&parseFloat(editSalaryVal)>0
                  ?<span className="text-sky-600 font-bold">Valor hora: {fmtMoney(Math.round(parseFloat(editSalaryVal)/parseFloat(editMonthlyHours)))}/h ({parseFloat(editSalaryVal).toLocaleString('es-AR')} ÷ {editMonthlyHours}h)</span>
                  :'Cuántas horas trabaja por mes — define el valor hora base'}
              </p>
            </div>
            <div className="space-y-2">
              <Input label="Valor hora extra fijo — opcional" type="number" value={editExtraRateVal} onChange={setEditExtraRateVal}
                placeholder={parseFloat(editMonthlyHours)>0&&parseFloat(editSalaryVal)>0
                  ?`Auto: ${fmtMoney(Math.round(parseFloat(editSalaryVal)/parseFloat(editMonthlyHours)))}/h`
                  :'Ej: 12000'}/>
              <p className="text-xs text-gray-400">
                {parseFloat(editExtraRateVal)>0
                  ?`Valor fijo para todas las horas extra: ${fmtMoney(parseFloat(editExtraRateVal))}/h`
                  :'Dejalo vacío para calcular automáticamente desde las horas mensuales'}
              </p>
            </div>
            <button onClick={saveSalary} className="w-full py-3 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>Guardar sueldo</button>
          </div>
        </Modal>
      )}
      {editRec&&<EditRecModal rec={editRec} onSave={handleSaveRec} onClose={()=>setEditRec(null)} adminId={profile.id} orgId={profile.organization_id}/>}

      {/* New Admin Modal */}
      {showNewAdmin&&<NewAdminModal roles={adminRoles} locations={locations}
        onClose={()=>setShowNewAdmin(false)}
        onSave={async(name,email,pw,perms,roleId,locIds)=>{
          try{
            if(!(await confirmEmailElsewhere(email)))return;
            const {data:newAdminId,error:se}=await supabase.rpc('admin_create_admin',{p_name:name,p_email:email,p_password:pw,p_perms:perms});
            if(se)throw se;
            if(newAdminId&&(roleId||locIds)){const {error:re}=await supabase.from('profiles').update({admin_role_id:roleId||null,admin_location_ids:locIds||null}).eq('id',newAdminId);if(re)throw re;}
            const emps=await getAllProfiles();
            setEmployees(emps||[]);
            setShowNewAdmin(false);showToast('Admin creado');
          }catch(e){showToast(e.message,'error');}
        }}
      />}

      {/* Edit Admin Permissions Modal */}
      {editAdminPerms&&<EditAdminPermsModal roles={adminRoles} locations={locations}
        admin={editAdminPerms}
        onClose={()=>setEditAdminPerms(null)}
        onSave={async(perms,isSuperAdmin,roleId,locIds)=>{
          try{
            const {error:ue}=await supabase.from('profiles').update({admin_permissions:perms,is_super_admin:isSuperAdmin,admin_role_id:roleId||null,admin_location_ids:locIds||null}).eq('id',editAdminPerms.id);
            if(ue)throw ue;
            const emps=await getAllProfiles();
            setEmployees(emps||[]);
            setEditAdminPerms(null);showToast('Permisos actualizados');
          }catch(e){showToast(e.message,'error');}
        }}
      />}
      {showNewRec&&<NewRecordModal employees={employees} defaultDate={(recFrom&&recFrom===recTo)?recFrom:localDateISO()} onSave={handleNewRec} onClose={()=>setShowNewRec(false)}/>}
      {auditRecId&&<AuditHistory recordId={auditRecId} onClose={()=>setAuditRecId(null)}/>}

      {payrollEmp&&(
        <PayrollModal emp={payrollEmp} month={analysisMonth} stats={getStats(payrollEmp.id)}
          extraHours={extraHoursList.filter(h=>h.employee_id===payrollEmp.id)}
          pending={pendingExtras.filter(h=>h.employee_id===payrollEmp.id)}
          advances={advList.filter(a=>a.employee_id===payrollEmp.id&&a.status!=='pending'&&a.status!=='rejected')}
          empSchedMap={empSchedMap}
          onClose={()=>setPayrollEmp(null)} onSave={handleSavePayroll}/>
      )}
      {editHol&&(
        <Modal open={true} onClose={()=>setEditHol(null)} title="Editar feriado">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Nombre del feriado</label>
              <input value={editHol.name} onChange={e=>setEditHol(p=>({...p,name:e.target.value}))}
                className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"/>
            </div>
            <div>
              <label className="block text-xs font-bold text-gray-500 mb-1.5 uppercase tracking-wide">Fecha</label>
              <input type="date" value={editHol.date} onChange={e=>setEditHol(p=>({...p,date:e.target.value}))}
                className="w-full px-4 py-2.5 rounded-2xl border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-sky-400"/>
            </div>
            <div className="flex gap-3 pt-1">
              <button onClick={()=>setEditHol(null)} className="flex-1 py-3 rounded-2xl text-sm font-bold border-2 border-gray-200 text-gray-500">Cancelar</button>
              <button onClick={handleEditHol} className="flex-1 py-3 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>Guardar</button>
            </div>
          </div>
        </Modal>
      )}
      <Modal open={showAddHol} onClose={()=>setShowAddHol(false)} title="Agregar feriado manual">
        <div className="space-y-4">
          <Input label="Fecha" type="date" value={holForm.date} onChange={v=>setHolForm(p=>({...p,date:v}))}/>
          <Input label="Nombre" value={holForm.name} onChange={v=>setHolForm(p=>({...p,name:v}))} placeholder="Ej: Rosh Hashaná"/>
          <button onClick={handleAddHol} className="w-full py-3 rounded-2xl text-sm font-bold text-white" style={{background:'var(--brand-grad)'}}>Guardar</button>
        </div>
      </Modal>
    </div>
  );
}

function ManualForm({employees,onSave}){
  const [empId,setEmpId]=useState('');const [date,setDate]=useState(localDateISO());
  const [status,setStatus]=useState('absent');const [just,setJust]=useState('');
  const [tIn,setTIn]=useState('');const [tOut,setTOut]=useState('');
  const send=()=>{
    if(!empId)return;
    if(status==='present'){
      if(!tIn){alert('Indicá la hora de entrada.');return;}
      const ci=new Date(date+'T'+tIn+':00');let co=tOut?new Date(date+'T'+tOut+':00'):null;
      if(co&&co<=ci)co=new Date(co.getTime()+86400000);
      const extra={check_in:ci.toISOString(),check_out:co?co.toISOString():null,check_in_method:'admin',minutes_late:0};
      if(co)extra.minutes_worked=Math.round((co-ci)/60000);
      onSave(empId,date,'present',just||null,extra);setTIn('');setTOut('');
    } else onSave(empId,date,status,just||null);
    setJust('');
  };
  return(
    <div className="bg-amber-50 border border-amber-100 rounded-3xl p-5 space-y-3">
      <p className="text-sm font-bold text-amber-800">Registrar falta/presencia manual</p>
      <div className="grid grid-cols-2 gap-2">
        <select value={empId} onChange={e=>setEmpId(e.target.value)} className="px-3 py-2.5 rounded-2xl border border-amber-200 text-sm bg-white focus:outline-none">
          <option value="">Empleado...</option>{employees.map(e=><option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
        <input type="date" value={date} onChange={e=>setDate(e.target.value)} className="px-3 py-2.5 rounded-2xl border border-amber-200 text-sm bg-white focus:outline-none"/>
      </div>
      <div className="flex gap-1.5">
        {[['present','Presente'],['absent','Falta'],['justified','Justificada'],['holiday','Feriado']].map(([v,l])=>(
          <button key={v} onClick={()=>setStatus(v)} className={`flex-1 py-2 rounded-xl text-xs font-bold ${status===v?'bg-amber-600 text-white':'bg-amber-100 text-amber-700'}`}>{l}</button>
        ))}
      </div>
      {status==='present'&&(
        <div className="grid grid-cols-2 gap-2">
          <label className="text-[11px] font-bold text-amber-700">Hora de entrada<input type="time" value={tIn} onChange={e=>setTIn(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-2xl border border-amber-200 text-sm bg-white focus:outline-none"/></label>
          <label className="text-[11px] font-bold text-amber-700">Hora de salida <span className="font-normal">(opcional)</span><input type="time" value={tOut} onChange={e=>setTOut(e.target.value)} className="mt-1 w-full px-3 py-2.5 rounded-2xl border border-amber-200 text-sm bg-white focus:outline-none"/></label>
        </div>
      )}
      {(status==='justified'||status==='absent'||status==='present')&&(
        <input value={just} onChange={e=>setJust(e.target.value)} placeholder={status==='present'?'Motivo de la carga manual (opcional)':'Motivo (opcional)'} className="w-full px-3.5 py-2.5 rounded-2xl border border-amber-200 text-sm bg-white focus:outline-none"/>
      )}
      <button onClick={send} disabled={!empId}
        className="w-full py-2.5 rounded-2xl text-sm font-bold text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50">Registrar</button>
    </div>
  );
}
